/* Support Inbox — in-house email support (Zendesk replacement). The wireframe UI
   is wired to the goserver /v2/support APIs here (§11): bootstrap → nav, list,
   thread, actions, notes, views and search are all driven by redux + the support
   saga (cms_token_3). The look/structure is unchanged from the wireframe; only
   the data sources are real now.

   Array-index keys are used only for stable, non-reordering render lists. The
   modal/overlay close-on-click + Escape pattern trips two jsx-a11y rules that are
   disabled for that pattern only. */
/* eslint-disable react/prop-types, react/no-array-index-key, jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import emojiData from '@emoji-mart/data';
import EmojiPicker from '@emoji-mart/react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  AlertCircle,
  AtSign,
  Check,
  CheckCheck,
  ChevronDown,
  ChevronRight,
  ChevronsUpDown,
  ChevronUp,
  Circle,
  Clock,
  Download,
  ExternalLink,
  File,
  FileSpreadsheet,
  FileText,
  Inbox,
  Link2,
  List,
  Mail,
  Maximize2,
  MessageCircle,
  MessageSquare,
  Minimize2,
  Paperclip,
  PanelLeftClose,
  PanelLeftOpen,
  Pencil,
  PenLine,
  Phone,
  Plus,
  Route,
  Search,
  Send,
  Settings,
  Smartphone,
  Smile,
  StickyNote,
  Trash2,
  User,
  Users,
  X
} from 'lucide-react';
import { modules as quillModules, formats as quillFormats } from '../../../constants';
import { getCookie } from '../../../util_functions';
import { openChatWithUserUUID } from '../../chat/chat';
import UserRole from '../../../myt_chat/chat/enums/UserRole';
import ConnectionState from '../../../myt_chat/chat/enums/ConnectionState';
import { getAuthUserInfoOrDefault } from '../../../myt_chat/network/cookie';
import { CallbackHandler } from '../../../myt_chat/chat/callbacks/handler';
import { ChatClient } from '../../../myt_chat/worker/chatProxies';
import SmsWindow from '../reports/sms_window';
import * as actions from '../../../redux/action_creators';
import {
  addTeamMember,
  apiErrorReason,
  canonicalBodyHtml,
  cleanHtml,
  contactAvatar,
  contactDisplayName,
  contactProfile,
  createRoutingRule,
  createTeam,
  DEFAULT_AVATAR,
  deleteRoutingRule,
  deleteTeam,
  downloadTranscript,
  extractMentions,
  fetchEligibleAgents,
  fetchMySignature,
  fetchRoutingRules,
  fetchTeams,
  htmlToPlainText,
  humanizeEvent,
  mapAgents,
  mapTagPool,
  mapTeams,
  OPERATOR_TOKEN,
  removeTeamMember,
  reorderRoutingRules,
  saveMySignature,
  shortAgo,
  stamp,
  textToHtmlBody,
  updateRoutingRule,
  uploadAttachment,
  useDropdown,
  useQuillPaste
} from './api_helpers';
import NewConversationModal from './new_conversation_modal';
import './support_inbox.css';

const FOLDER_ICONS = {
  user: User,
  at: AtSign,
  circle: Circle,
  list: List
};

// Left-nav badge tooltip/aria text. All folder + team counts from bootstrap are
// OPEN-conversation counts (backend excludes resolved/closed), so we spell that
// out; the Mentions folder instead counts conversations with unseen @mentions
// for the current agent. Singular/plural handled explicitly.
const openCountLabel = (n) => `${n} open conversation${n === 1 ? '' : 's'}`;
const mentionsCountLabel = (n) => `${n} unseen mention${n === 1 ? '' : 's'}`;

// The Default Inbox nav folder: conversations with no team (team_uuid null/empty).
// The goserver list API resolves folder=default_inbox to that scope.
const DEFAULT_INBOX_KEY = 'default_inbox';

// Conversation-list pagination. LIST_PAGE_SIZE mirrors the goserver default
// (support.defaultListLimit) — the page the list opens on; "Load more" grows the
// requested `limit` by one page at a time. LIST_MAX_LIMIT mirrors that endpoint's
// hard per-request ceiling (support.maxListLimit): asking for more is silently
// clamped server-side, so the button stops there rather than spinning forever.
// Raising the ceiling is a one-line goserver change (app/services/support/api_read.go).
const LIST_PAGE_SIZE = 25;
const LIST_MAX_LIMIT = 500;

// cms_user.access_type is a MySQL SET (comma-separated) so a user may hold
// SUPER_USER alongside other roles — split and match the exact token, mirroring
// the goserver support.isSuperUser check that gates the team mutations server-side.
const isSuperUserAccess = (accessType) =>
  (accessType || '')
    .split(',')
    .map((tok) => tok.trim().toUpperCase())
    .includes('SUPER_USER');

// Fields a Create View filter condition can match on. `name` is the exact
// whitelist key the goserver filter compiler understands (viewFieldColumn) and
// is what gets persisted/sent to the backend — never change it or saved views
// break. `label` (when present) overrides the display text in the picker; it is
// purely cosmetic.
const FILTER_FIELD_DEFS = [
  { name: 'Status', desc: 'Lifecycle state of the conversation' },
  { name: 'Priority', desc: 'Urgency level set on the ticket' },
  { name: 'Assign team', label: 'Assigned Team', desc: 'Team inbox the conversation belongs to' },
  {
    name: 'Assign agent',
    label: 'Assigned Agent',
    desc: 'Agent the conversation is assigned to'
  },
  { name: 'User Type', desc: 'Whether the contact is a student or a teacher' },
  { name: 'Email', label: 'User Email', desc: "Contact's email address on the conversation" },
  {
    name: 'Support Email',
    desc: 'Which of our inbox addresses the customer emailed'
  },
  {
    name: 'Student Membership',
    desc: 'Membership status of the linked student'
  },
  { name: 'Tags', desc: 'Tags applied to the conversation' },
  { name: 'Created at', desc: 'When the conversation was started' },
  { name: 'Waiting since', desc: 'How long the contact has been waiting' },
  { name: 'Snoozed until', desc: 'When a snoozed conversation reopens' },
  { name: 'Last message at', desc: 'Time of the most recent message' },
  { name: 'Last interaction at', desc: 'Last activity by agent or contact' },
  { name: 'Resolved at', desc: 'When the conversation was resolved' },
  { name: 'First reply at', desc: 'Time of the first agent reply' }
];

// Display label for a field key (falls back to the key itself). Keeps the
// picker/trigger text decoupled from the backend field key.
const fieldLabel = (name) => (FILTER_FIELD_DEFS.find((f) => f.name === name) || {}).label || name;

// User Type filter values. `label` is shown in the value dropdown; the lowercase
// token is what the compiler expects (normalizeUserTypeValue).
const USER_TYPE_OPTIONS = [
  { key: 'student', label: 'Student' },
  { key: 'teacher', label: 'Teacher' }
];

// Support Email filter values — which of our inbox addresses the customer
// emailed. `key` is the exact reply_to_group address the backend expects
// (normalizeSupportEmailValue); `label` is shown in the value dropdown and maps
// back to the address on save (like Assign team/agent).
const SUPPORT_EMAIL_OPTIONS = [
  { key: 'care@myyogateacher.com', label: 'Care – students (care@myyogateacher.com)' },
  { key: 'teacher-care@myyogateacher.com', label: 'Teacher Care (teacher-care@myyogateacher.com)' },
  {
    key: 'teachers_payment@myyogateacher.com',
    label: 'Teacher Payments (teachers_payment@myyogateacher.com)'
  }
];

// Student Membership filter values — derived state of the linked student.
// `key` is the canonical token the backend expects (normalizeMembershipValue);
// `label` is shown in the value dropdown and maps back to the key on save (like
// Support Email / Assign team-agent).
const MEMBERSHIP_OPTIONS = [
  { key: 'trial', label: 'Trial' },
  { key: 'member', label: 'Member' },
  // "NomNot" = not a member AND not in trial. ("non-member" is intentionally not
  // used — it is ambiguous, since a trial user is also technically not a member.)
  { key: 'nomnot', label: 'NomNot' }
];

const BASE_OPERATORS = ['equals', 'not equals', 'set', 'not set'];
const DATE_OPERATORS = [...BASE_OPERATORS, 'greater than', 'less than', 'between'];
const DATE_FIELDS = new Set([
  'Created at',
  'Waiting since',
  'Snoozed until',
  'Last message at',
  'Last interaction at',
  'Resolved at',
  'First reply at'
]);
// User Type is a derived enum field; the backend only compiles equals/not_equals
// for it (set/not_set are meaningless for a boolean-ish derived predicate).
const ENUM_ONLY_OPERATORS = ['equals', 'not equals'];
// Multi-select operators — the conversation value matches ANY (contains) / NONE
// (not contains) of the selected values. Only valid for options-type fields.
const CONTAINS_OPERATORS = ['contains', 'not contains'];
// Derived enum fields: backend compiles only equals/not_equals (+ contains).
const DERIVED_ENUM_FIELDS = new Set(['User Type', 'Support Email', 'Student Membership']);
// Plain column enum/options fields (equals/not_equals/set/not_set + contains).
const COLUMN_ENUM_FIELDS = new Set(['Status', 'Priority', 'Assign team', 'Assign agent']);
const operatorsForField = (field) => {
  if (DATE_FIELDS.has(field)) return DATE_OPERATORS;
  if (DERIVED_ENUM_FIELDS.has(field)) return [...ENUM_ONLY_OPERATORS, ...CONTAINS_OPERATORS];
  if (COLUMN_ENUM_FIELDS.has(field)) return [...BASE_OPERATORS, ...CONTAINS_OPERATORS];
  return BASE_OPERATORS;
};

// Conversation lifecycle statuses. `key` is the backend enum value; `css` maps
// to the --status-*-bg/-text token pair (the "in_progress" enum uses the
// legacy "progress" css key).
const STATUS_OPTIONS = [
  { label: 'Open', key: 'open', desc: 'New or reopened, needs attention' },
  { label: 'In Progress', key: 'in_progress', desc: 'An agent is working on it' },
  { label: 'Waiting', key: 'waiting', desc: 'Waiting on the customer to reply' },
  { label: 'Snoozed', key: 'snoozed', desc: 'Hidden until the snooze time' },
  { label: 'Resolved', key: 'resolved', desc: 'Solved; a reply reopens it' },
  { label: 'Closed', key: 'closed', desc: 'Archived; new emails start fresh' }
];
const cssStatus = (key) => (key === 'in_progress' ? 'progress' : key);
const statusLabel = (key) => (STATUS_OPTIONS.find((s) => s.key === key) || {}).label || 'Open';

// Ticket priorities. `key` maps to the --priority-* token; 'none' clears it.
const PRIORITY_OPTIONS = [
  { key: 'urgent', label: 'Urgent' },
  { key: 'high', label: 'High' },
  { key: 'medium', label: 'Medium' },
  { key: 'low', label: 'Low' },
  { key: 'none', label: 'No priority' }
];
const priorityLabel = (key) => (PRIORITY_OPTIONS.find((p) => p.key === key) || {}).label || '';

// Statuses offered in the split Send button's "Send and set as" dropup.
const SEND_STATUS_OPTIONS = STATUS_OPTIONS.filter((s) => s.key !== 'snoozed');

// Status filter offered in the list header (Active = server default scope).
const STATUS_FILTER_OPTIONS = [
  { label: 'Active', key: '', desc: 'Everything not resolved or closed' },
  ...STATUS_OPTIONS
];

// Sort orders for the conversation list header → backend sort tokens.
const SORT_OPTIONS = [
  { label: 'Oldest activity', key: 'oldest_activity' },
  { label: 'Newest activity', key: 'newest_activity' },
  { label: 'Started first', key: 'started_first' },
  { label: 'Started last', key: 'started_last' },
  { label: 'Waiting longest', key: 'waiting_longest' },
  { label: 'Priority first', key: 'priority_first' }
];

// `set` / `not set` are existence checks — no value input needed.
const operatorNeedsValue = (op) => !!op && op !== 'set' && op !== 'not set';
// contains / not contains take a multi-select (array of values).
const operatorIsMulti = (op) => op === 'contains' || op === 'not contains';

// Compute the condition patch when the operator changes, converting the value
// shape sensibly between single (value) and multi (values) modes so switching
// operators never drops the whole selection or crashes the editor.
const operatorPatch = (cond, operator) => {
  const wasMulti = operatorIsMulti(cond.operator);
  const nowMulti = operatorIsMulti(operator);
  if (nowMulti && wasMulti) return { operator }; // multi → multi: keep values
  if (nowMulti && !wasMulti) {
    return { operator, values: cond.value ? [cond.value] : [], value: '', value2: '' };
  }
  if (!nowMulti && wasMulti) {
    return { operator, value: (cond.values && cond.values[0]) || '', values: [], value2: '' };
  }
  return { operator, value: '', value2: '' }; // single → single (existing reset)
};

let filterRowSeq = 0;
const newCondition = () => {
  filterRowSeq += 1;
  return { id: `cond_${filterRowSeq}`, field: '', operator: '', value: '', value2: '', values: [] };
};
const newGroup = () => {
  filterRowSeq += 1;
  return { id: `grp_${filterRowSeq}`, conditions: [newCondition()] };
};

// Inverse of OPERATOR_TOKEN: backend token → UI spelling. Used when opening an
// existing view for editing, to turn the persisted underscore tokens back into
// the space-separated labels the operator picker renders.
const TOKEN_OPERATOR = Object.entries(OPERATOR_TOKEN).reduce((acc, [ui, token]) => {
  acc[token] = ui;
  return acc;
}, {});

// Inverse of CreateViewModal.mapValueForField — turn a persisted canonical value
// back into the UI label the value dropdown shows, so an edited view seeds with
// the right selections. Only the four fields that map label→key/uuid on save
// need inverting; Status/Priority/User Type labels, free text and dates round-
// trip unchanged (they were stored as-is).
const unmapValueForField = (field, value, teams, agents) => {
  if (field === 'Assign team') {
    const t = teams.find((x) => x.key === value);
    return t ? t.label : value;
  }
  if (field === 'Assign agent') {
    const a = agents.find((x) => x.key === value);
    return a ? a.label : value;
  }
  if (field === 'Support Email') {
    const s = SUPPORT_EMAIL_OPTIONS.find((x) => x.key === value);
    return s ? s.label : value;
  }
  if (field === 'Student Membership') {
    const m = MEMBERSHIP_OPTIONS.find((x) => x.key === value);
    return m ? m.label : value;
  }
  return value;
};

// Reverse-map a persisted view's filters JSON into editor group/condition state
// (the exact inverse of CreateViewModal.buildPayload). Operator tokens become UI
// spellings, canonical values become labels, and the contains / not contains
// multi operators repopulate the values[] array. Always returns at least one
// (empty) group so the editor is never blank.
const seedGroupsFromFilters = (filters, teams, agents) => {
  let parsed = filters;
  if (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed);
    } catch (e) {
      parsed = null;
    }
  }
  const srcGroups = (parsed && parsed.groups) || [];
  const outGroups = srcGroups
    .map((g) => {
      const conditions = (g.conditions || []).map((c) => {
        const operator = TOKEN_OPERATOR[c.operator] || c.operator;
        const multi = operatorIsMulti(operator);
        return {
          ...newCondition(),
          field: c.field || '',
          operator,
          value: multi ? '' : unmapValueForField(c.field, c.value || '', teams, agents),
          value2: c.value2 || '',
          values: multi
            ? (c.values || []).map((v) => unmapValueForField(c.field, v, teams, agents))
            : []
        };
      });
      return { ...newGroup(), conditions };
    })
    .filter((g) => g.conditions.length > 0);
  return outGroups.length ? outGroups : [newGroup()];
};

const isImageAttachment = (attachment) =>
  String(attachment.content_type || '')
    .toLowerCase()
    .startsWith('image/');

// Copy text to the clipboard. Prefers the async Clipboard API; falls back to a
// hidden textarea + execCommand for older/insecure-context browsers. Resolves
// on success, rejects otherwise so callers can toast accordingly.
const copyToClipboard = (text) => {
  if (navigator.clipboard && window.isSecureContext) {
    return navigator.clipboard.writeText(text);
  }
  return new Promise((resolve, reject) => {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      if (ok) resolve();
      else reject(new Error('copy command failed'));
    } catch (err) {
      reject(err);
    }
  });
};

// -------------------------------------------------------------------- nav rail
// `canManageViews` gates the "Create view" affordance only (super-user, matching
// the view-management rules). The Support settings button is NOT gated — every
// agent needs it for their own signature.
function NavRail({
  folders,
  teams,
  views,
  activeFolder,
  onSelectFolder,
  onCreateView,
  onDeleteView,
  onEditView,
  onSearch,
  onNewConversation,
  canManageViews,
  onOpenSettings
}) {
  const [teamOpen, setTeamOpen] = useState(true);
  const [viewsOpen, setViewsOpen] = useState(true);
  return (
    <nav className="si-col si-nav">
      <div className="si-nav-brand">
        <span className="si-nav-brand-title">
          <Inbox size={20} /> Inbox
        </span>
        <div className="si-nav-brand-actions">
          {/* Settings opens for EVERY agent — the popup's Signature section is a
              per-agent setting. The super-user-only sections inside it (Teams,
              Routing rules) are filtered by SettingsModal. */}
          <button
            type="button"
            className="si-nav-search"
            aria-label="Support settings"
            title="Support settings"
            onClick={onOpenSettings}
          >
            <Settings size={18} />
          </button>
          <button type="button" className="si-nav-search" aria-label="Search" onClick={onSearch}>
            <Search size={18} />
          </button>
        </div>
      </div>

      <button type="button" className="si-nav-new" onClick={onNewConversation}>
        <Plus size={16} />
        <span className="si-nav-label">New conversation</span>
      </button>

      {folders.map((f) => {
        const IconEl = FOLDER_ICONS[f.icon] || List;
        return (
          <button
            key={f.key}
            type="button"
            className={`si-nav-item ${activeFolder === f.key ? 'si-active' : ''}`}
            onClick={() => onSelectFolder(f.key)}
          >
            <IconEl size={16} />
            <span className="si-nav-label">{f.label}</span>
            {f.count > 0 && (
              <span
                className="si-nav-count"
                data-tooltip-id="my-tooltip"
                data-tooltip-content={
                  f.key === 'mentions' ? mentionsCountLabel(f.count) : openCountLabel(f.count)
                }
                data-tooltip-delay-show={300}
                aria-label={
                  f.key === 'mentions' ? mentionsCountLabel(f.count) : openCountLabel(f.count)
                }
              >
                {f.count}
              </span>
            )}
          </button>
        );
      })}

      <div className="si-section-title si-section-title-row">
        <span>Inboxes</span>
        <button
          type="button"
          className="si-section-add"
          aria-label={teamOpen ? 'Collapse Inboxes' : 'Expand Inboxes'}
          onClick={() => setTeamOpen((v) => !v)}
        >
          {teamOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
        </button>
      </div>
      {teamOpen && (
        <div className="si-nav-group">
          {teams.length === 0 && <div className="si-state-msg">No teams</div>}
          {teams.map((t) => (
            <button
              key={t.key}
              type="button"
              className={`si-nav-item ${activeFolder === t.key ? 'si-active' : ''}`}
              onClick={() => onSelectFolder(t.key)}
            >
              <span className="si-nav-emoji">{t.emoji || <List size={14} />}</span>
              <span className="si-nav-label">{t.label}</span>
              {t.count > 0 && (
                <span
                  className="si-nav-count"
                  data-tooltip-id="my-tooltip"
                  data-tooltip-content={openCountLabel(t.count)}
                  data-tooltip-delay-show={300}
                  aria-label={openCountLabel(t.count)}
                >
                  {t.count}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      <div className="si-section-title si-section-title-row">
        <span className="si-section-left">
          Views
          {canManageViews && (
            <button
              type="button"
              className="si-section-add"
              aria-label="Create view"
              onClick={onCreateView}
            >
              <Plus size={15} />
            </button>
          )}
        </span>
        <button
          type="button"
          className="si-section-add"
          aria-label={viewsOpen ? 'Collapse Views' : 'Expand Views'}
          onClick={() => setViewsOpen((v) => !v)}
        >
          {viewsOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
        </button>
      </div>
      {viewsOpen && (
        <div className="si-nav-group">
          {views.map((v) => (
            <div
              key={v.key}
              role="button"
              tabIndex={0}
              className={`si-nav-item si-nav-view ${activeFolder === v.key ? 'si-active' : ''}`}
              onClick={() => onSelectFolder(v.key)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSelectFolder(v.key);
                }
              }}
            >
              <span className="si-nav-label">{v.label}</span>
              {v.count > 0 && (
                <span
                  className="si-nav-count"
                  data-tooltip-id="my-tooltip"
                  data-tooltip-content={openCountLabel(v.count)}
                  data-tooltip-delay-show={300}
                  aria-label={openCountLabel(v.count)}
                >
                  {v.count}
                </span>
              )}
              {v.canDelete && (
                <button
                  type="button"
                  className="si-nav-view-edit"
                  aria-label={`Edit view ${v.label}`}
                  title="Edit view"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditView(v);
                  }}
                >
                  <Pencil size={14} />
                </button>
              )}
              {v.canDelete && (
                <button
                  type="button"
                  className="si-nav-view-del"
                  aria-label={`Delete view ${v.label}`}
                  title="Delete view"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteView(v);
                  }}
                >
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </nav>
  );
}

// ------------------------------------------------------------- list header bits
// Status filter trigger + menu. The menu also carries the "Unread only" toggle in
// a separated section at the top: unread is a modifier on top of whatever status
// is picked (Active+Unread, Closed+Unread are all valid), so it sits above a
// divider rather than among the mutually exclusive status rows.
function StatusFilterDropdown({ value, onChange, count, unreadOnly, onUnreadOnly }) {
  const { open, setOpen, ref } = useDropdown();
  const current = STATUS_FILTER_OPTIONS.find((s) => s.key === value) || STATUS_FILTER_OPTIONS[0];
  return (
    <div className="si-sort" ref={ref}>
      <button
        type="button"
        className={`si-filter-btn ${unreadOnly ? 'si-active' : ''}`}
        onClick={() => setOpen((o) => !o)}
      >
        {count} {current.label}
        {unreadOnly ? ' · Unread' : ''} <ChevronDown size={14} />
      </button>
      {open && (
        <div className="si-status-menu si-sort-menu" role="listbox">
          {/* Unread narrows whatever folder + status is active to conversations
              with messages THIS agent hasn't read (server-side `unread=1`). */}
          <button
            type="button"
            className={`si-status-item si-status-toggle ${unreadOnly ? 'si-active' : ''}`}
            aria-pressed={unreadOnly}
            onClick={() => {
              onUnreadOnly(!unreadOnly);
              setOpen(false);
            }}
          >
            <span className={`si-check ${unreadOnly ? 'si-check-on' : ''}`} aria-hidden="true" />
            <span className="si-status-toggle-text">
              <span className="si-fieldpick-name">Unread only</span>
              <span className="si-fieldpick-desc">
                Show only conversations with unread messages
              </span>
            </span>
          </button>
          <div className="si-menu-sep" />
          {STATUS_FILTER_OPTIONS.map((s) => (
            <button
              key={s.key || 'active'}
              type="button"
              className={`si-status-item si-status-item-desc ${s.key === value ? 'si-active' : ''}`}
              onClick={() => {
                onChange(s.key);
                setOpen(false);
              }}
            >
              <span className="si-fieldpick-name">{s.label}</span>
              {s.desc && <span className="si-fieldpick-desc">{s.desc}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SortDropdown({ value, onChange }) {
  const { open, setOpen, ref } = useDropdown();
  const current = SORT_OPTIONS.find((s) => s.key === value) || SORT_OPTIONS[1];
  return (
    <div className="si-sort" ref={ref}>
      <button type="button" className="si-filter-btn" onClick={() => setOpen((o) => !o)}>
        {current.label} <ChevronDown size={14} />
      </button>
      {open && (
        <div className="si-status-menu si-sort-menu si-sort-menu-end" role="listbox">
          {SORT_OPTIONS.map((o) => (
            <button
              key={o.key}
              type="button"
              className={`si-status-item ${o.key === value ? 'si-active' : ''}`}
              onClick={() => {
                onChange(o.key);
                setOpen(false);
              }}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ListSkeleton() {
  return (
    <>
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} className="si-skeleton-row">
          <div className="si-skeleton-avatar" />
          <div className="si-skeleton-lines">
            <div className="si-skeleton-line" />
            <div className="si-skeleton-line short" />
          </div>
        </div>
      ))}
    </>
  );
}

// Shared avatar image with a single fallback: renders `url` when set, else the
// backend default profile image. If the photo URL is broken/expired, onError
// swaps to the default once (the data-fallback flag guards against a loop when
// even the default fails). Keeps the caller's className/alt for styling.
function Avatar({ url, alt = '', className }) {
  return (
    <img
      className={className}
      src={url || DEFAULT_AVATAR}
      alt={alt}
      onError={(e) => {
        if (e.currentTarget.dataset.fallback) return;
        e.currentTarget.dataset.fallback = '1';
        e.currentTarget.src = DEFAULT_AVATAR;
      }}
    />
  );
}

function ConversationList({
  rows,
  status,
  selectedId,
  onSelect,
  navCollapsed,
  onToggleNav,
  title,
  statusFilter,
  onStatusFilter,
  unreadOnly,
  onUnreadOnly,
  sort,
  onSort,
  count,
  hasMore,
  loadingMore,
  onLoadMore,
  atServerCap
}) {
  return (
    <div className="si-col si-list">
      <div className="si-list-head">
        <button
          type="button"
          className="si-collapse-btn"
          aria-label={navCollapsed ? 'Expand inbox bar' : 'Collapse inbox bar'}
          onClick={onToggleNav}
        >
          {navCollapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
        </button>
        {title}
      </div>
      <div className="si-list-filters">
        <StatusFilterDropdown
          value={statusFilter}
          onChange={onStatusFilter}
          count={count}
          unreadOnly={unreadOnly}
          onUnreadOnly={onUnreadOnly}
        />
        <SortDropdown value={sort} onChange={onSort} />
      </div>

      {status === 'loading' && rows.length === 0 && <ListSkeleton />}
      {status === 'fail' && <div className="si-state-msg">Couldn&apos;t load conversations.</div>}
      {status === 'success' && rows.length === 0 && (
        <div className="si-state-msg">No conversations here.</div>
      )}

      {rows.map((c) => (
        <div
          key={c.id}
          className={`si-conv ${selectedId === c.id ? 'si-active' : ''} ${c.unread > 0 ? 'si-unread' : ''}`}
          role="button"
          tabIndex={0}
          onClick={() => onSelect(c.id)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onSelect(c.id);
            }
          }}
        >
          <Avatar className="si-avatar" url={c.avatar} alt={c.name} />
          <div className="si-conv-body">
            <div className="si-conv-top">
              {c.priority && c.priority !== 'none' && (
                <span
                  className={`si-prio-dot si-prio-${c.priority}`}
                  title={`${priorityLabel(c.priority)} priority`}
                />
              )}
              <span className="si-conv-name">{c.name}</span>
              <span className="si-conv-channel">{c.channel}</span>
              <span className="si-conv-time">{c.lastActivity}</span>
            </div>
            <div className="si-conv-preview">{c.preview}</div>
            <div className="si-conv-meta">
              {c.tags.map((tag) => (
                <span key={tag} className="si-tag si-tag-neutral">
                  {tag}
                </span>
              ))}
              {c.unread > 0 && <span className="si-pill">{c.unread > 9 ? '9+' : c.unread}</span>}
            </div>
          </div>
        </div>
      ))}

      {/* Pagination footer. The list opens on one page (LIST_PAGE_SIZE rows) and
          grows a page at a time; the fetch re-requests rows 1..N with a bigger
          `limit`, so the rows already on screen stay mounted (the skeleton only
          replaces an EMPTY list) and every background refresh — realtime packet,
          post-reply reconcile — keeps the expanded page. `atServerCap` is the
          backend's own per-request ceiling (maxListLimit): once there we say so
          rather than showing a button that can't do anything. */}
      {status !== 'loading' && rows.length > 0 && atServerCap && count > rows.length && (
        <div className="si-list-more">
          <span className="si-list-more-note">
            Showing the {rows.length} most recent of {count}. Narrow the list with the filters or
            search to find older conversations.
          </span>
        </div>
      )}
      {hasMore && (
        <div className="si-list-more">
          <button type="button" className="si-btn" onClick={onLoadMore} disabled={loadingMore}>
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
          <span className="si-list-more-note">
            {rows.length} of {count}
          </span>
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------- mention textarea
// Plain textarea with an @-autocomplete over the bootstrap agents. Selecting a
// name inserts "@Full Name "; the parent turns those tokens into the explicit
// {cms_user_uuid,name} mentions array on save (extractMentions).
function MentionTextarea({
  value,
  onChange,
  agents,
  placeholder,
  className,
  onSubmit,
  autoFocus,
  onMentionOpen
}) {
  const [query, setQuery] = useState(null); // null = closed
  const taRef = useRef(null);

  const suggestions =
    query === null
      ? []
      : agents.filter((a) => a.label.toLowerCase().includes(query.toLowerCase())).slice(0, 6);

  const handleChange = (e) => {
    const v = e.target.value;
    onChange(v);
    const caret = e.target.selectionStart;
    const upto = v.slice(0, caret);
    const m = upto.match(/@([\p{L}][\p{L}\s.-]{0,30})?$/u);
    if (m) {
      // Opening the autocomplete: give the parent a chance to refresh the
      // agent list (throttled there) so newly added CMS users can be mentioned
      // without a page reload.
      if (query === null && onMentionOpen) onMentionOpen();
      setQuery(m[1] || '');
    } else setQuery(null);
  };

  const pick = (agent) => {
    const el = taRef.current;
    const caret = el ? el.selectionStart : value.length;
    const upto = value.slice(0, caret);
    const rest = value.slice(caret);
    const replaced = upto.replace(/@([\p{L}][\p{L}\s.-]{0,30})?$/u, `@${agent.label} `);
    onChange(replaced + rest);
    setQuery(null);
    if (el) el.focus();
  };

  return (
    <div className="si-mention-wrap">
      {query !== null && suggestions.length > 0 && (
        <div className="si-mention-pop">
          {suggestions.map((a) => (
            <button
              key={a.key}
              type="button"
              className="si-mention-item"
              onMouseDown={(e) => {
                e.preventDefault();
                pick(a);
              }}
            >
              {a.label}
              {a.you && <span className="si-opt-muted"> (You)</span>}
            </button>
          ))}
        </div>
      )}
      <textarea
        ref={taRef}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={autoFocus}
        className={className}
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        onKeyDown={(e) => {
          if (onSubmit && e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            onSubmit();
          }
        }}
      />
    </div>
  );
}

// -------------------------------------------------------------------- composer
function Composer({ conversation, agents, refresh, dispatch, onPatch, onMentionOpen }) {
  const { uuid } = conversation;
  const [tab, setTab] = useState('reply');
  const [text, setText] = useState('');
  const [note, setNote] = useState('');
  const [to, setTo] = useState(conversation.contact ? conversation.contact.email : '');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [showBcc, setShowBcc] = useState(false);
  const [files, setFiles] = useState([]); // {name, id?, progress, failed}
  const [noteFiles, setNoteFiles] = useState([]); // {name, id?, progress, failed}
  const [expanded, setExpanded] = useState(false);
  const [richHtml, setRichHtml] = useState('');
  const [sending, setSending] = useState(false);
  const submittedRef = useRef(false);
  const noteSubmittedRef = useRef(false);
  const fileRef = useRef(null);
  const quillRef = useRef(null);
  const emoji = useDropdown();

  const reply_status = useSelector((s) => s.support.reply_status);
  const note_status = useSelector((s) => s.support.note_status);

  const richActive = expanded && tab === 'reply';
  useQuillPaste(quillRef, richActive);

  // Reset the To field when switching conversations.
  useEffect(() => {
    setTo(conversation.contact ? conversation.contact.email : '');
    setText('');
    setNote('');
    setRichHtml('');
    setFiles([]);
    setNoteFiles([]);
  }, [uuid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear the reply draft once the saga confirms a successful send.
  useEffect(() => {
    if (submittedRef.current && reply_status === 'success') {
      setText('');
      setRichHtml('');
      setFiles([]);
      setSending(false);
      setExpanded(false);
      submittedRef.current = false;
    } else if (submittedRef.current && reply_status === 'fail') {
      setSending(false);
      submittedRef.current = false; // keep the draft so it can be re-sent
    }
  }, [reply_status]);

  useEffect(() => {
    if (noteSubmittedRef.current && note_status === 'success') {
      setNote('');
      setNoteFiles([]);
      setExpanded(false);
      noteSubmittedRef.current = false;
    } else if (noteSubmittedRef.current && note_status === 'fail') {
      noteSubmittedRef.current = false;
    }
  }, [note_status]);

  const onFilePick = (e) => {
    const setActiveFiles = tab === 'note' ? setNoteFiles : setFiles;
    const picked = Array.from(e.target.files || []);
    picked.forEach((file) => {
      const entry = { name: file.name, id: null, progress: 0, failed: false };
      setActiveFiles((prev) => [...prev, entry]);
      uploadAttachment(file, (pct) =>
        setActiveFiles((prev) => prev.map((f) => (f === entry ? { ...f, progress: pct } : f)))
      )
        .then((res) =>
          setActiveFiles((prev) =>
            prev.map((f) =>
              f.name === entry.name && !f.id ? { ...f, id: res.id, progress: 100 } : f
            )
          )
        )
        .catch(() =>
          setActiveFiles((prev) =>
            prev.map((f) => (f.name === entry.name && !f.id ? { ...f, failed: true } : f))
          )
        );
    });
    e.target.value = '';
  };
  const removeFile = (idx) => {
    const setActiveFiles = tab === 'note' ? setNoteFiles : setFiles;
    setActiveFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const insertEmoji = (native) => {
    if (tab === 'note') setNote((n) => n + native);
    else if (richActive) setRichHtml((h) => h + native);
    else setText((t) => t + native);
    emoji.setOpen(false);
  };

  // Mini textarea ↔ expanded Quill hand-over. Both helpers are 1:1 on newlines
  // (one line ⇄ one block, a blank line ⇄ an empty block), so switching editors
  // never adds or drops spacing.
  const toggleExpanded = () => {
    if (!expanded && tab === 'reply') setRichHtml(textToHtmlBody(text));
    if (expanded && tab === 'reply') setText(htmlToPlainText(richHtml));
    setExpanded((e) => !e);
  };
  const switchTab = (next) => {
    if (expanded) {
      if (tab === 'reply' && next === 'note') setText(htmlToPlainText(richHtml));
      if (tab === 'note' && next === 'reply') setRichHtml(textToHtmlBody(text));
    }
    setTab(next);
  };

  useEffect(() => {
    if (!expanded) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') toggleExpanded();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  });

  const splitList = (v) =>
    v
      .split(',')
      .map((x) => x.trim())
      .filter(Boolean);

  // A reply is "empty" when the body has no text after trimming AND there are no
  // (fully uploaded) attachments. richHtml carries markup even when visually blank
  // (e.g. `<p><br></p>`), so measure its plain-text projection, not the raw HTML.
  const replyAttachmentIds = files.filter((f) => f.id).map((f) => f.id);
  const replyIsEmpty =
    (richActive ? htmlToPlainText(richHtml).trim() : text.trim()) === '' &&
    replyAttachmentIds.length === 0;

  const doSendReply = (setStatus) => {
    // Empty composer: never send an empty email. If a status was chosen from the
    // dropup, apply it as a pure status change (same PatchConversation path the
    // header status dropdown uses); a plain Send is a no-op.
    if (replyIsEmpty) {
      if (setStatus && onPatch) onPatch({ status: setStatus });
      return;
    }
    // Canonical newline representation on the wire (see api_helpers): one block
    // per visual line, never a `<p>` whose default margins would double it.
    const bodyHtml = richActive ? canonicalBodyHtml(cleanHtml(richHtml)) : textToHtmlBody(text);
    const bodyText = richActive ? htmlToPlainText(richHtml) : text;
    submittedRef.current = true;
    setSending(true);
    dispatch(
      actions.send_support_reply({
        uuid,
        body: {
          to: splitList(to),
          cc: splitList(cc),
          bcc: splitList(bcc),
          body_html: bodyHtml,
          body_text: bodyText,
          attachment_ids: replyAttachmentIds,
          send_and_set_status: setStatus || ''
        },
        refresh
      })
    );
  };

  const doAddNote = () => {
    const body = note.trim();
    const attachment_ids = noteFiles.filter((f) => f.id).map((f) => f.id);
    if (!body && attachment_ids.length === 0) return;
    noteSubmittedRef.current = true;
    dispatch(
      actions.add_support_note({
        uuid,
        body,
        mentions: extractMentions(body, agents),
        attachment_ids
      })
    );
  };

  const activeFiles = tab === 'note' ? noteFiles : files;
  const anyUploading = activeFiles.some((f) => !f.id && !f.failed);

  let editor;
  if (tab === 'note') {
    editor = (
      <MentionTextarea
        className="si-textarea"
        placeholder="Add an internal note (not sent to customer). Type @ to mention a teammate…"
        value={note}
        onChange={setNote}
        agents={agents}
        onSubmit={doAddNote}
        onMentionOpen={onMentionOpen}
      />
    );
  } else if (richActive) {
    editor = (
      <ReactQuill
        ref={quillRef}
        className="si-quill"
        theme="snow"
        value={richHtml}
        onChange={setRichHtml}
        modules={quillModules}
        formats={quillFormats}
        placeholder="Write a reply…"
      />
    );
  } else {
    editor = (
      <textarea
        className="si-textarea"
        placeholder="Write a reply…"
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
    );
  }

  const body = (
    <>
      <div className="si-composer-tabs-row">
        <div className="si-composer-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'reply'}
            className={`si-tab ${tab === 'reply' ? 'si-active' : ''}`}
            onClick={() => switchTab('reply')}
          >
            Reply
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'note'}
            className={`si-tab ${tab === 'note' ? 'si-active' : ''}`}
            onClick={() => switchTab('note')}
          >
            Private note
          </button>
        </div>
        <button
          type="button"
          className="si-tool-btn si-composer-expand"
          aria-label={expanded ? 'Minimize composer' : 'Expand composer'}
          onClick={toggleExpanded}
        >
          {expanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
        </button>
      </div>

      {tab === 'reply' && (
        <>
          <div className="si-field">
            <span className="si-field-label">To:</span>
            <input value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <div className="si-field">
            <span className="si-field-label">Cc:</span>
            <input
              placeholder="Email addresses separated by comma"
              value={cc}
              onChange={(e) => setCc(e.target.value)}
            />
            <button type="button" className="si-bcc-btn" onClick={() => setShowBcc((v) => !v)}>
              {showBcc ? 'Remove BCC' : 'BCC'}
            </button>
          </div>
          {showBcc && (
            <div className="si-field">
              <span className="si-field-label">Bcc:</span>
              <input
                placeholder="Email addresses separated by comma"
                value={bcc}
                onChange={(e) => setBcc(e.target.value)}
              />
            </div>
          )}
        </>
      )}

      {editor}

      {activeFiles.length > 0 && (
        <div className="si-attachments">
          {activeFiles.map((f, i) => (
            <span key={`${f.name}-${i}`} className="si-tag-chip si-att-chip">
              <Paperclip size={12} /> {f.name}
              {f.failed && <span className="si-att-progress si-att-failed">failed</span>}
              {!f.failed && !f.id && <span className="si-att-progress">{f.progress}%</span>}
              <button type="button" aria-label={`Remove ${f.name}`} onClick={() => removeFile(i)}>
                <X size={13} />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="si-composer-foot">
        <button
          type="button"
          className="si-tool-btn"
          aria-label="Attach file"
          onClick={() => fileRef.current && fileRef.current.click()}
        >
          <Paperclip size={16} />
        </button>
        <input ref={fileRef} type="file" multiple hidden onChange={onFilePick} />

        <div className="si-emoji" ref={emoji.ref}>
          <button
            type="button"
            className="si-tool-btn"
            aria-label="Insert emoji"
            onClick={() => emoji.setOpen((o) => !o)}
          >
            <Smile size={16} />
          </button>
          {emoji.open && (
            <div className="si-emoji-pop">
              <EmojiPicker
                data={emojiData}
                onEmojiSelect={(e) => insertEmoji(e.native)}
                theme={document.documentElement.classList.contains('dark') ? 'dark' : 'light'}
                previewPosition="none"
                skinTonePosition="none"
              />
            </div>
          )}
        </div>

        {tab === 'reply' ? (
          <SendSplit
            label={sending ? 'Sending…' : 'Send'}
            menuTitle="Send and set as"
            disabled={sending || anyUploading}
            sendDisabled={replyIsEmpty}
            onSend={() => doSendReply('')}
            onSendAndSet={(status) => doSendReply(status)}
          />
        ) : (
          <button
            type="button"
            className="si-btn si-btn-primary si-send-main"
            disabled={note_status === 'loading' || anyUploading}
            onClick={doAddNote}
          >
            <Send size={14} /> Add note
          </button>
        )}
      </div>
    </>
  );

  if (expanded) {
    return (
      <div className="si-composer-modal-overlay">
        <div className="si-composer si-composer-modal">{body}</div>
      </div>
    );
  }
  return <div className="si-composer">{body}</div>;
}

// Split Send button: primary send + a caret opening the "set status" dropup.
// `sendDisabled` gates only the primary Send (e.g. empty composer — nothing to
// email); the caret stays enabled because "Send and set as X" on an empty
// composer is a valid pure status change, not a send.
function SendSplit({ label, menuTitle, onSend, onSendAndSet, disabled, sendDisabled }) {
  const { open, setOpen, ref } = useDropdown();
  return (
    <div className="si-send-split" ref={ref}>
      <button
        type="button"
        className="si-btn si-btn-primary si-send-main"
        disabled={disabled || sendDisabled}
        onClick={onSend}
      >
        <Send size={14} /> {label}
      </button>
      <button
        type="button"
        className="si-btn si-btn-primary si-send-caret"
        aria-label="Send and set status"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
      >
        <ChevronDown size={14} />
      </button>
      {open && (
        <div className="si-send-menu">
          <div className="si-send-menu-title">{menuTitle}</div>
          {SEND_STATUS_OPTIONS.map((s) => (
            <button
              key={s.key}
              type="button"
              className="si-send-menu-item"
              onClick={() => {
                setOpen(false);
                onSendAndSet(s.key);
              }}
            >
              {s.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusDropdown({ status, onChange }) {
  const { open, setOpen, ref } = useDropdown();
  return (
    <div className="si-status" ref={ref}>
      <button
        type="button"
        className={`si-status-trigger si-status-${cssStatus(status)}`}
        onClick={() => setOpen((o) => !o)}
      >
        {statusLabel(status)} <ChevronDown size={14} />
      </button>
      {open && (
        <div className="si-status-menu" role="listbox">
          {STATUS_OPTIONS.map((s) => (
            <button
              key={s.key}
              type="button"
              className={`si-status-item ${s.key === status ? 'si-active' : ''}`}
              onClick={() => {
                setOpen(false);
                if (s.key !== status) onChange(s.key);
              }}
            >
              <span className={`si-status-dot si-status-${cssStatus(s.key)}`} />
              {s.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Thread-header quick actions (copy link / chat / SMS-WhatsApp / call). One
// shared definition list — {key, label, Icon, show, onClick} — consumed by BOTH
// the inline icon row (wide header) and the "..." overflow menu (narrow
// header), so gating can never diverge between the two placements. Reuses the
// app-wide mechanisms: the chat popup (ChatWindowManager) and outbound Twilio
// (GlobalOutboundCallManager) are mounted globally in home/index.jsx;
// SMS/WhatsApp uses the shared SmsWindow modal rendered once by ThreadHeader.
// Gating (brief §2): Copy link always; Chat only with a linked student;
// SMS/WhatsApp only with a phone + real dispatch method; Call only with a phone.
function buildQuickActions({ student, conversationUuid, dispatch, openSms, requestCall }) {
  const studentUuid = student && student.uuid;
  const method = student && student.message_dispatch_method;
  const hasPhone = !!(student && student.phone && String(student.phone).trim());
  // Only the two real channels enable the SMS/WhatsApp action; 'none'/null/other hide it.
  const canMessage = hasPhone && (method === 'sms' || method === 'whatsapp');
  const isWhatsapp = method === 'whatsapp';

  // Copy a shareable deep link to this ticket. The link is handled by the
  // inbox on mount via the `tkt` query param.
  const copyShareLink = () => {
    if (!conversationUuid) return;
    const link = `${window.location.origin}/support?tkt=${conversationUuid}`;
    copyToClipboard(link)
      .then(() => dispatch(actions.set_notification_variable(true, 'success', 'Link copied')))
      .catch(() =>
        dispatch(actions.set_notification_variable(true, 'error', 'Could not copy link'))
      );
  };

  const openChat = () =>
    openChatWithUserUUID(student.uuid, UserRole.STUDENT, {
      name: student.name || '',
      avatar: (student && student.avatar) || DEFAULT_AVATAR,
      role: UserRole.STUDENT
    });

  // Fat-finger guard (brief): never dial straight from the button — hand the
  // target to ThreadHeader so it can raise the confirmation modal. The actual
  // 'twilio-outgoing-call-start' dispatch happens only on explicit confirm.
  const startCall = () => {
    if (!hasPhone) return;
    requestCall({
      studentUuid: student.uuid,
      studentName: student.name || '',
      phone: String(student.phone).trim()
    });
  };

  return [
    { key: 'copy_link', label: 'Copy link', Icon: Link2, show: true, onClick: copyShareLink },
    { key: 'chat', label: 'Chat', Icon: MessageSquare, show: !!studentUuid, onClick: openChat },
    {
      key: 'message',
      label: isWhatsapp ? 'WhatsApp' : 'SMS',
      Icon: isWhatsapp ? MessageCircle : Smartphone,
      show: canMessage,
      onClick: openSms
    },
    { key: 'call', label: 'Call', Icon: Phone, show: hasPhone, onClick: startCall }
  ].filter((a) => a.show);
}

// Fat-finger confirmation for placing an outbound call. Reuses the file's own
// modal pattern (see CreateViewModal): si-modal-overlay/si-modal, Escape and
// click-outside both cancel, and neither places the call. Only the explicit
// "Call" button confirms, and it self-disables the instant it's clicked so a
// rapid double-tap can never queue two calls.
function CallConfirmModal({ target, onConfirm, onCancel }) {
  const [calling, setCalling] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const confirm = () => {
    if (calling) return;
    setCalling(true);
    onConfirm();
  };

  const name = (target.studentName || '').trim();
  const { phone } = target;
  let prompt = 'Start an outbound call?';
  if (name && phone) prompt = `Call ${name} at ${phone}?`;
  else if (name) prompt = `Call ${name}?`;
  else if (phone) prompt = `Call ${phone}?`;

  return (
    <div className="si-modal-overlay" role="presentation" onClick={onCancel}>
      <div
        className="si-modal si-modal-confirm"
        role="dialog"
        aria-modal="true"
        aria-label="Confirm call"
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="si-modal-close" aria-label="Close" onClick={onCancel}>
          <X size={20} />
        </button>

        <h2 className="si-modal-title">Place call?</h2>
        <p className="si-modal-sub">{prompt}</p>

        <div className="si-confirm-foot">
          <button type="button" className="si-btn-cancel" onClick={onCancel} disabled={calling}>
            Cancel
          </button>
          <button type="button" className="si-btn-call" onClick={confirm} disabled={calling}>
            <Phone size={16} /> Call
          </button>
        </div>
      </div>
    </div>
  );
}

// Inline icon-button row for the quick actions — shown only when the header is
// wide enough (see ThreadHeader's ResizeObserver).
function QuickActionsRow({ quickActions }) {
  if (quickActions.length === 0) return null;
  return (
    <div className="si-head-actions">
      {quickActions.map(({ key, label, Icon, onClick }) => (
        <button
          key={key}
          type="button"
          className="si-head-action"
          title={label}
          aria-label={label}
          onClick={onClick}
        >
          <Icon size={17} />
        </button>
      ))}
    </div>
  );
}

// "..." overflow menu. Always offers Download Transcript; additionally lists
// the quick actions when the header is narrow (`quickActions` is empty when the
// inline row is visible, so actions are never duplicated). The menu closes
// after any action is chosen.
function MoreMenu({ quickActions, onDownload }) {
  const { open, setOpen, ref } = useDropdown();
  // Run an action and close the menu (matches the existing item behavior).
  const run = (fn) => {
    setOpen(false);
    fn();
  };
  return (
    <div className="si-status" ref={ref}>
      <button
        type="button"
        className="si-btn"
        aria-label="More actions"
        onClick={() => setOpen((o) => !o)}
      >
        •••
      </button>
      {open && (
        <div className="si-status-menu" role="menu">
          {quickActions.map(({ key, label, Icon, onClick }) => (
            <button key={key} type="button" className="si-status-item" onClick={() => run(onClick)}>
              <Icon size={15} /> {label}
            </button>
          ))}
          <button type="button" className="si-status-item" onClick={() => run(onDownload)}>
            <Download size={15} /> Download Transcript
          </button>
        </div>
      )}
    </div>
  );
}

// Below this header width the inline quick-action row is folded into the "..."
// menu. Derived from the widest realistic wide-state layout: 40px horizontal
// padding + 36px avatar + name/assignee block (~180px before truncation) +
// priority badge (~90px) + 4 icon buttons (4×32 + gaps = 140px) + status
// dropdown (~120px) + "..." button (~44px) + flex gaps (~60px) ≈ 710px, with
// the icons themselves accounting for ~150px of that — so fold below 640px,
// where the remaining controls still fit comfortably.
const THREAD_HEADER_NARROW_PX = 640;

// Thread header. Owns the responsive `narrow` flag (ResizeObserver on the
// header element — panel widths don't track the viewport, so a media query
// won't do) plus the single SmsWindow instance either placement opens.
function ThreadHeader({ conv, assignee, student, contactLink, avatar, displayName, onPatch }) {
  const dispatch = useDispatch();
  const headRef = useRef(null);
  const [narrow, setNarrow] = useState(false);
  const [smsOpen, setSmsOpen] = useState(false);
  const [callTarget, setCallTarget] = useState(null); // {studentUuid, studentName, phone} | null
  const studentUuid = student && student.uuid;

  // Close the SMS modal and any pending call confirmation when the thread
  // switches to a different student.
  useEffect(() => {
    setSmsOpen(false);
    setCallTarget(null);
  }, [studentUuid]);

  // Fire the outbound-call event only after the agent explicitly confirms.
  const confirmCall = () => {
    if (!callTarget) return;
    window.dispatchEvent(
      new CustomEvent('twilio-outgoing-call-start', {
        detail: { studentUuid: callTarget.studentUuid, studentName: callTarget.studentName }
      })
    );
    setCallTarget(null);
  };

  // Measure before paint so the header renders in the right mode on first
  // frame (no inline-row flash on narrow panels).
  useLayoutEffect(() => {
    const el = headRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const check = (width) => setNarrow(width < THREAD_HEADER_NARROW_PX);
    check(el.getBoundingClientRect().width);
    const ro = new ResizeObserver((entries) => {
      if (entries[0]) check(entries[0].contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const quickActions = buildQuickActions({
    student,
    conversationUuid: conv.uuid,
    dispatch,
    openSms: () => setSmsOpen(true),
    requestCall: setCallTarget
  });

  return (
    <div className="si-thread-head" ref={headRef}>
      <Avatar className="si-avatar" url={avatar} alt="" />
      <div className="si-thread-headinfo">
        <span className="si-thread-title">
          <ContactName href={contactLink.href} type={contactLink.type}>
            {displayName}
          </ContactName>
          {conv.ticket_number && <span className="si-thread-ticket">#{conv.ticket_number}</span>}
        </span>
        {conv.subject && (
          <span
            className="si-thread-subject"
            data-tooltip-id="my-tooltip"
            data-tooltip-content={conv.subject}
            data-tooltip-delay-show={300}
          >
            {conv.subject}
          </span>
        )}
        <span className="si-thread-assignee">
          {assignee ? `Agent: ${assignee.label.split(' ')[0]}` : 'Unassigned'}
        </span>
      </div>
      <PriorityBadge prio={conv.priority} />
      {!narrow && <QuickActionsRow quickActions={quickActions} />}
      <div className="si-thread-actions">
        <StatusDropdown status={conv.status} onChange={(status2) => onPatch({ status: status2 })} />
        <MoreMenu
          quickActions={narrow ? quickActions : []}
          onDownload={() => downloadTranscript(conv.uuid, conv.ticket_number, 'txt')}
        />
      </div>
      {smsOpen && student && (
        <SmsWindow
          student_uuid={student.uuid}
          student_name={student.name || ''}
          close_fn={() => setSmsOpen(false)}
          message_dispatch_method={student.message_dispatch_method}
          is_allowed_whatsapp={student.message_dispatch_method === 'whatsapp'}
          is_allowed_sms={student.message_dispatch_method === 'sms'}
          student_iana_timezone={student.iana_timezone || ''}
        />
      )}
      {callTarget && (
        <CallConfirmModal
          target={callTarget}
          onConfirm={confirmCall}
          onCancel={() => setCallTarget(null)}
        />
      )}
    </div>
  );
}

function PriorityBadge({ prio }) {
  if (!prio || prio === 'none') return null;
  return (
    <span className={`si-prio-badge si-prio-${prio}`}>
      <span className={`si-prio-dot si-prio-${prio}`} />
      {priorityLabel(prio)}
    </span>
  );
}

const renderAgent = (a) => (
  <span className="si-opt">
    <span>
      {a.label}
      {a.you && <span className="si-opt-muted"> (You)</span>}
    </span>
  </span>
);
const renderTeam = (t) => (
  <span className="si-opt">
    {t.emoji && <span className="si-opt-emoji">{t.emoji}</span>}
    {t.label}
  </span>
);
const renderPriority = (p) => (
  <span className="si-opt">
    {p.key === 'none' ? (
      <span className="si-prio-dot si-prio-none" />
    ) : (
      <span className={`si-prio-dot si-prio-${p.key}`} />
    )}
    {p.label}
  </span>
);

// Minimum room (px) an open menu needs below the trigger before it flips above.
const SELECT_MENU_MIN = 200;

// Combobox: trigger showing the current value + a searchable option list. The
// menu is absolutely positioned inside a scroll pane (the settings content pane,
// the right-nav details column) whose `overflow-y: auto` would otherwise clip it.
// On open we measure the trigger against its nearest scrollable ancestor (the
// real clip box, not just the viewport) and either drop the menu below or flip
// it above — whichever has more room — capping its height to that room so the
// option list always stays fully visible with its own internal scroll.
function SearchSelect({ value, options, placeholder, onChange, renderItem }) {
  const { open, setOpen, ref } = useDropdown();
  const triggerRef = useRef(null);
  const [query, setQuery] = useState('');
  const [menu, setMenu] = useState({ up: false, maxHeight: null });
  const q = query.trim().toLowerCase();
  const filtered = options.filter((o) => o.label.toLowerCase().includes(q));
  const choose = (o) => {
    onChange(o);
    setOpen(false);
    setQuery('');
  };

  useLayoutEffect(() => {
    if (!open) {
      setQuery('');
      return;
    }
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    // Walk up to the nearest clipping ancestor; its box is the real boundary.
    let clipTop = 0;
    let clipBottom = window.innerHeight;
    for (let node = el.parentElement; node; node = node.parentElement) {
      const oy = window.getComputedStyle(node).overflowY;
      if (oy === 'auto' || oy === 'scroll' || oy === 'hidden') {
        const cr = node.getBoundingClientRect();
        clipTop = cr.top;
        clipBottom = cr.bottom;
        break;
      }
    }
    const margin = 8;
    const spaceBelow = clipBottom - rect.bottom - margin;
    const spaceAbove = rect.top - clipTop - margin;
    const up = spaceBelow < SELECT_MENU_MIN && spaceAbove > spaceBelow;
    setMenu({ up, maxHeight: Math.max(160, Math.floor(up ? spaceAbove : spaceBelow)) });
  }, [open]);

  return (
    <div className="si-select" ref={ref}>
      <button
        ref={triggerRef}
        type="button"
        className="si-select-trigger"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="si-select-value">
          {value ? renderItem(value) : <span className="si-select-ph">{placeholder}</span>}
        </span>
        <ChevronsUpDown size={15} className="si-select-caret" />
      </button>
      {open && (
        <div
          className={`si-select-menu ${menu.up ? 'si-select-menu-up' : ''}`}
          style={menu.maxHeight ? { maxHeight: `${menu.maxHeight}px` } : undefined}
        >
          <div className="si-select-search">
            <Search size={14} />
            <input
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              value={query}
              placeholder="Search…"
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="si-select-options">
            {filtered.map((o) => (
              <button
                key={o.key}
                type="button"
                className={`si-select-option ${value && o.key === value.key ? 'si-active' : ''}`}
                onClick={() => choose(o)}
              >
                {renderItem(o)}
              </button>
            ))}
            {filtered.length === 0 && <div className="si-select-empty">No matches</div>}
          </div>
        </div>
      )}
    </div>
  );
}

// Tag picker: type to filter existing tags, or create a new one on Enter / click.
function TagsInput({ tags, pool, onChange }) {
  const { open, setOpen, ref } = useDropdown();
  const [query, setQuery] = useState('');
  const q = query.trim();
  const suggestions = pool.filter(
    (t) => t.toLowerCase().includes(q.toLowerCase()) && !tags.includes(t)
  );
  const exists =
    tags.some((t) => t.toLowerCase() === q.toLowerCase()) ||
    pool.some((t) => t.toLowerCase() === q.toLowerCase());
  const add = (t) => {
    const v = t.trim();
    if (v && !tags.includes(v)) onChange([...tags, v]);
    setQuery('');
  };
  const remove = (t) => onChange(tags.filter((x) => x !== t));
  return (
    <div className="si-tags-field" ref={ref}>
      <div className="si-tags-box">
        {tags.map((t) => (
          <span key={t} className="si-tag-chip">
            {t}
            <button type="button" aria-label={`Remove ${t}`} onClick={() => remove(t)}>
              <X size={13} />
            </button>
          </span>
        ))}
        <input
          className="si-tags-input"
          value={query}
          placeholder={tags.length ? '' : 'Select tags'}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add(query);
            }
            if (e.key === 'Backspace' && !query && tags.length) {
              remove(tags[tags.length - 1]);
            }
          }}
        />
      </div>
      {open && (q.length > 0 || suggestions.length > 0) && (
        <div className="si-select-menu si-tags-menu">
          <div className="si-select-options">
            {suggestions.map((t) => (
              <button key={t} type="button" className="si-select-option" onClick={() => add(t)}>
                <span className="si-tag-chip si-tag-chip-plain">{t}</span>
              </button>
            ))}
            {q.length > 0 && !exists && (
              <button type="button" className="si-select-option" onClick={() => add(q)}>
                <Plus size={14} /> Create &ldquo;{q}&rdquo;
              </button>
            )}
            {q.length === 0 && suggestions.length === 0 && (
              <div className="si-select-empty">No tags</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------- thread
// Per-channel source badge. `icon` is a lucide component; `css` maps to a
// --si-src-* colour token pair (see support_inbox.css / tokens.css). Unknown
// sources fall back to email styling so future channels still render a badge.
const SOURCE_META = {
  email: { icon: Mail, css: 'email', label: 'Email' },
  whatsapp: { icon: MessageCircle, css: 'whatsapp', label: 'WhatsApp' },
  sms: { icon: Smartphone, css: 'sms', label: 'SMS' },
  chat: { icon: MessageSquare, css: 'chat', label: 'Chat' },
  note: { icon: StickyNote, css: 'note', label: 'Private note' }
};

function SourceIcon({ source }) {
  const meta = SOURCE_META[source] || SOURCE_META.email;
  const Ico = meta.icon;
  return (
    <span className={`si-src-icon si-src-${meta.css}`} title={meta.label} aria-label={meta.label}>
      <Ico size={13} />
    </span>
  );
}

// Full-size viewer for an inline image attachment. Follows the file's modal
// pattern (si-modal-overlay scrim, click-outside / Escape / × all close) but
// frameless — the image itself is the content, capped at 90vw/90vh. Also carries
// an explicit "Open in new tab" escape hatch on the signed attachment URL for
// zooming/saving in the browser's own image viewer.
function ImageLightbox({ src, alt, downloadHref, onClose }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="si-modal-overlay si-lightbox" role="presentation" onClick={onClose}>
      <button
        type="button"
        className="si-modal-close si-lightbox-close"
        aria-label="Close image"
        onClick={onClose}
      >
        <X size={22} />
      </button>
      <div
        className="si-lightbox-body"
        role="dialog"
        aria-modal="true"
        aria-label={alt}
        onClick={(e) => e.stopPropagation()}
      >
        <img className="si-lightbox-image" src={src} alt={alt} />
        <span className="si-lightbox-actions">
          <a className="si-lightbox-open" href={src} target="_blank" rel="noopener noreferrer">
            <ExternalLink size={13} /> Open in new tab
          </a>
          <a className="si-lightbox-open" href={downloadHref || src} download={alt}>
            <Download size={13} /> Download
          </a>
        </span>
      </div>
    </div>
  );
}

// File-type classification for non-image attachment cards: {icon, accent class,
// short type label}. Keyed off the extension first (most reliable — inbound mail
// often reports application/octet-stream) with a mime fallback.
const ATTACHMENT_KINDS = {
  pdf: { Icon: FileText, css: 'pdf', label: 'PDF' },
  sheet: { Icon: FileSpreadsheet, css: 'sheet', label: 'Spreadsheet' },
  doc: { Icon: FileText, css: 'doc', label: 'Document' },
  file: { Icon: File, css: 'file', label: 'File' }
};

const ATTACHMENT_EXT_KINDS = {
  pdf: 'pdf',
  xls: 'sheet',
  xlsx: 'sheet',
  csv: 'sheet',
  tsv: 'sheet',
  ods: 'sheet',
  doc: 'doc',
  docx: 'doc',
  odt: 'doc',
  rtf: 'doc',
  txt: 'doc'
};

function attachmentKind(attachment) {
  const name = String(attachment.file_name || '');
  const dot = name.lastIndexOf('.');
  const ext = dot > -1 ? name.slice(dot + 1).toLowerCase() : '';
  const type = String(attachment.content_type || '').toLowerCase();
  let key = ATTACHMENT_EXT_KINDS[ext];
  if (!key) {
    const isSheet = type.includes('sheet') || type.includes('excel') || type.includes('csv');
    if (type.includes('pdf')) key = 'pdf';
    else if (isSheet) key = 'sheet';
    else if (type.includes('word') || type.startsWith('text/')) key = 'doc';
    else key = 'file';
  }
  const kind = ATTACHMENT_KINDS[key];
  // Prefer the real extension as the type label (matches Zendesk: "… / PDF");
  // fall back to the generic label when the filename carries no extension.
  return { ...kind, label: ext ? ext.toUpperCase() : kind.label };
}

// The signed URL to hand a "save this file" affordance. download_url is the same
// S3 object signed with a response-content-disposition override so the browser
// saves it under the original file_name (the storage key is a UUID, and the
// plain `url` is signed for inline viewing). Falls back to the inline URL on
// older cached payloads that predate the field.
function attachmentDownloadURL(attachment) {
  return attachment.download_url || attachment.url;
}

// Zendesk-style attachment card for a non-image attachment: large file-type icon
// on top, filename + type label below. The whole card is the open affordance —
// it's an <a target="_blank"> on the signed URL, so click / middle-click /
// keyboard all work without extra handlers. The corner glyph is a second link on
// the download-signed URL, so it saves under the real filename instead of the
// UUID key. It sits outside the card <a> (nested anchors are invalid) — the
// wrapper is what the flex/hover rules key off.
function AttachmentCard({ attachment }) {
  const { Icon, css, label } = attachmentKind(attachment);
  const name = attachment.file_name || 'Attachment';
  return (
    <span className="si-attach-card-wrap">
      <a
        className="si-attach-card"
        href={attachment.url}
        target="_blank"
        rel="noopener noreferrer"
        title={`${name} — open in new tab`}
      >
        <span className={`si-attach-card-icon si-attach-${css}`}>
          <Icon size={30} />
        </span>
        <span className="si-attach-card-meta">
          <span className="si-attach-card-name">{name}</span>
          <span className="si-attach-card-type">{label}</span>
        </span>
      </a>
      <a
        className="si-attach-card-download"
        href={attachmentDownloadURL(attachment)}
        download={name}
        aria-label={`Download ${name}`}
        title={`Download ${name}`}
      >
        <Download size={13} />
      </a>
    </span>
  );
}

// One inline image attachment. Clicking the thumbnail (a real <button>, so it's
// keyboard-reachable) opens the lightbox; a corner expand glyph appears on hover
// to advertise that it's clickable. Shared by message bubbles and internal notes
// so both behave identically.
function AttachmentImage({ attachment }) {
  const [open, setOpen] = useState(false);
  const alt = attachment.file_name || 'Attached image';
  return (
    <>
      <button
        type="button"
        className="si-msg-attach-thumb"
        title={`${alt} — click to view full size`}
        onClick={() => setOpen(true)}
      >
        <img className="si-msg-attach-image" src={attachment.url} alt={alt} loading="lazy" />
        <span className="si-msg-attach-expand" aria-hidden="true">
          <Maximize2 size={13} />
        </span>
      </button>
      {open && (
        <ImageLightbox
          src={attachment.url}
          alt={alt}
          downloadHref={attachmentDownloadURL(attachment)}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

// Internal note rendered inline in the thread — amber card, distinct from the
// inbound/outbound message bubbles.
function NoteAttachments({ attachments = [] }) {
  if (attachments.length === 0) return null;
  const imageAttachments = attachments.filter(isImageAttachment);
  const fileAttachments = attachments.filter((attachment) => !isImageAttachment(attachment));
  return (
    <>
      {imageAttachments.map((attachment) => (
        <AttachmentImage key={attachment.id} attachment={attachment} />
      ))}
      {fileAttachments.length > 0 && (
        <div className="si-note-attachments si-attach-cards">
          {fileAttachments.map((attachment) => (
            <AttachmentCard key={attachment.id} attachment={attachment} />
          ))}
        </div>
      )}
    </>
  );
}

function NoteBubble({ note, agentsByUuid }) {
  const author = note.author_cms_uuid && agentsByUuid[note.author_cms_uuid];
  return (
    <div className="si-msg si-msg-note">
      <span className="si-msg-author">
        <SourceIcon source="note" /> {author ? author.label : 'Agent'} · Private note
      </span>
      <div className="si-bubble si-bubble-note">
        {note.body}
        <NoteAttachments attachments={note.attachments} />
      </div>
      <span className="si-msg-time">{stamp(note.created_at)}</span>
    </div>
  );
}

// WhatsApp-style delivery indicator for OUTBOUND messages only. Pure mapping of
// the message's delivery lifecycle → {Icon (lucide), className (theme-token
// colour), title (hover tooltip)}. Returns null for inbound messages / notes
// and for unknown states, so callers render nothing. Precedence: failure states
// first (a bounced/failed mail can't have been opened), then engagement
// (opened_at, set by the Resend webhook backend), then plain delivery. Codes
// defensively for opened_at being absent until the backend deploys it.
const deliveryTick = (message) => {
  if (!message || message.direction !== 'outbound') return null;
  const status = message.delivery_status;
  const FAILURE_LABELS = { bounced: 'Bounced', complained: 'Marked as spam', failed: 'Failed' };
  if (FAILURE_LABELS[status]) {
    const label = FAILURE_LABELS[status];
    return {
      Icon: AlertCircle,
      className: 'si-tick si-tick-failed',
      title: message.delivery_error ? `${label}: ${message.delivery_error}` : label
    };
  }
  if (message.opened_at) {
    return {
      Icon: CheckCheck,
      className: 'si-tick si-tick-opened',
      title: `Opened ${stamp(message.opened_at)}`
    };
  }
  switch (status) {
    case 'delivered':
      return { Icon: CheckCheck, className: 'si-tick si-tick-muted', title: 'Delivered' };
    case 'sent':
      return { Icon: Check, className: 'si-tick si-tick-muted', title: 'Sent' };
    case 'queued':
      return { Icon: Clock, className: 'si-tick si-tick-muted', title: 'Queued' };
    default:
      return null;
  }
};

// Engagement webhooks (Resend) fire on EVERY open/click, so a customer
// re-reading an email floods the thread with identical "Customer opened the
// email" rows. Render-side collapse only (backend data untouched): drop an
// email_opened / email_clicked event when it's CONSECUTIVE (adjacent after the
// chronological sort) with the previous kept event, for the same event type +
// same referenced message, with created_at in the same minute. The FIRST event
// of each run is kept (it carries the earliest open time, which is the
// meaningful one).
const ENGAGEMENT_EVENT_TYPES = new Set(['email_opened', 'email_clicked']);

const engagementDedupKey = (item) => {
  let { payload } = item;
  if (typeof payload === 'string') {
    try {
      payload = JSON.parse(payload);
    } catch (e) {
      payload = {};
    }
  }
  payload = payload || {};
  const msgRef = payload.message_uuid || payload.message_id || payload.email_message_id || '';
  const d = new Date(item.created_at || '');
  // Minute-granularity bucket; fall back to the raw string if unparseable.
  const minute = Number.isNaN(d.getTime())
    ? String(item.created_at || '')
    : Math.floor(d.getTime() / 60000);
  return `${item.type}|${msgRef}|${minute}`;
};

const collapseEngagementRuns = (items) => {
  const out = [];
  let prevKey = null;
  items.forEach((item) => {
    const isEngagement = item.kind === 'event' && ENGAGEMENT_EVENT_TYPES.has(item.type);
    const key = isEngagement ? engagementDedupKey(item) : null;
    if (isEngagement && key !== null && key === prevKey) return; // duplicate in run — drop
    out.push(item);
    prevKey = key; // non-engagement items reset the run (consecutive-only collapse)
  });
  return out;
};

function MessageBubble({ message, contactName, agentsByUuid = {} }) {
  const isAgent = message.direction === 'outbound';
  // Inbound → the contact's name. Outbound → the actual sending agent's display
  // name (resolved from author_cms_uuid via the bootstrap agents), falling back
  // to the raw email, then "Agent". System-authored messages (e.g. the auto-
  // acknowledgement) have no agent, so they show "System".
  let author;
  if (!isAgent) {
    author = contactName || message.author_email;
  } else if (message.author_type === 'system') {
    author = 'System';
  } else {
    const agent = message.author_cms_uuid && agentsByUuid[message.author_cms_uuid];
    author = (agent && agent.label) || message.author_email || 'Agent';
  }
  const html = message.body_format === 'html' && message.body_html;
  const attachments = message.attachments || [];
  const imageAttachments = attachments.filter(isImageAttachment);
  const fileAttachments = attachments.filter((attachment) => !isImageAttachment(attachment));
  const tick = deliveryTick(message);
  const TickIcon = tick && tick.Icon;
  return (
    <div className={`si-msg ${isAgent ? 'si-msg-agent' : ''}`}>
      <span className="si-msg-author">
        <SourceIcon source={message.source} /> {author}
      </span>
      <div className="si-bubble">
        {html ? (
          <div
            className="si-msg-body"
            // Sanitized by DOMPurify (cleanHtml) before render.
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: cleanHtml(message.body_html) }}
          />
        ) : (
          // Text-format bodies (plain-text inbound mail) carry `\n`; pre-wrap is
          // the single mechanism that renders them — never also `\n`→`<br>`.
          <div className="si-msg-text">{message.body_text}</div>
        )}
        {imageAttachments.map((a) => (
          <AttachmentImage key={a.id} attachment={a} />
        ))}
      </div>
      {fileAttachments.length > 0 && (
        <div className="si-msg-attach si-attach-cards">
          {fileAttachments.map((a) => (
            <AttachmentCard key={a.id} attachment={a} />
          ))}
        </div>
      )}
      {isAgent && message.delivery_status === 'failed' && (
        <span className="si-msg-failed">
          Delivery failed{message.delivery_error ? `: ${message.delivery_error}` : ''}
        </span>
      )}
      <span className="si-msg-time">
        {stamp(message.created_at)}
        {tick && (
          <span className={tick.className} title={tick.title}>
            <TickIcon size={14} />
          </span>
        )}
      </span>
    </div>
  );
}

// Contact display name. When the contact is a linked student/teacher the name
// links to their CMS profile in a new tab; guests render as plain text (with a
// subtle badge). `href` comes from contactProfile(data.contact).
function ContactName({ href, type, className, children }) {
  if (!href) {
    return (
      <span className={className}>
        {children}
        {type === 'guest' && <span className="si-guest-badge">Guest</span>}
      </span>
    );
  }
  return (
    <a
      className={`${className} si-contact-link`}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      title="Open profile in a new tab"
    >
      {children}
    </a>
  );
}

function Thread({ data, status, agentsByUuid, agents, refresh, dispatch, onPatch, onMentionOpen }) {
  // Merge the separate `notes` array into the timeline so private notes appear
  // inline, in chronological order, as amber note cards. Notes are tagged
  // kind:'note'; existing events/messages keep their shape. Stable sort by
  // created_at (messages carry it on item.message). Consecutive
  // email_opened/email_clicked duplicates are then collapsed for render
  // (collapseEngagementRuns) — the server data is untouched.
  const thread = useMemo(() => {
    if (!data) return [];
    const timeline = data.timeline || [];
    const notes = data.notes || [];
    const tsOf = (it) => it.created_at || (it.message && it.message.created_at) || '';
    const merged = [
      ...timeline,
      ...notes.map((n) => ({ kind: 'note', id: n.id, created_at: n.created_at, note: n }))
    ].sort((a, b) => String(tsOf(a)).localeCompare(String(tsOf(b))));
    return collapseEngagementRuns(merged);
  }, [data]);

  // Auto-scroll. The container pins to the bottom on initial load / thread
  // switch, and follows new entries ONLY while the agent is already at (or
  // within 150px of) the bottom — never yanking the view away from history
  // they scrolled up to read. `nearBottomRef` is kept fresh by onScroll, so it
  // reflects the position BEFORE the DOM grew.
  const scrollRef = useRef(null);
  const nearBottomRef = useRef(true);
  const prevConvUuidRef = useRef(null);
  const convUuid = (data && data.conversation && data.conversation.uuid) || null;
  const lastEntry = thread.length > 0 ? thread[thread.length - 1] : null;
  const lastEntryKey = lastEntry ? `${lastEntry.kind || 'msg'}:${lastEntry.id}` : '';

  const onThreadScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    nearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight <= 150;
  };

  // Before paint so the pinned position never flashes mid-thread.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const switched = prevConvUuidRef.current !== convUuid;
    prevConvUuidRef.current = convUuid;
    if (switched) nearBottomRef.current = true; // fresh thread always starts pinned
    if (switched || nearBottomRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [convUuid, thread.length, lastEntryKey]);

  if (status === 'loading' && !data) {
    return (
      <div className="si-col si-thread">
        <div className="si-thread-spinner">
          <div className="si-spinner" />
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="si-col si-thread" style={{ alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ color: 'var(--text-muted)' }}>Select a conversation</span>
      </div>
    );
  }

  const conv = data.conversation;
  const assignee = conv.assignee_cms_uuid && agentsByUuid[conv.assignee_cms_uuid];
  const student = data.contact && data.contact.student;
  const contactLink = contactProfile(data.contact);

  return (
    <div className="si-col si-thread">
      <ThreadHeader
        conv={conv}
        assignee={assignee}
        student={student}
        contactLink={contactLink}
        avatar={contactAvatar(data.contact)}
        displayName={contactDisplayName(conv.contact, data.contact)}
        onPatch={onPatch}
      />

      <div className="si-thread-scroll" ref={scrollRef} onScroll={onThreadScroll}>
        {thread.length === 0 && <div className="si-state-msg">No messages yet.</div>}
        {thread.map((item) => {
          if (item.kind === 'event') {
            return (
              <div key={`e-${item.id}`} className="si-event">
                {humanizeEvent(item, agentsByUuid)} · {stamp(item.created_at)}
              </div>
            );
          }
          if (item.kind === 'note') {
            return <NoteBubble key={`n-${item.id}`} note={item.note} agentsByUuid={agentsByUuid} />;
          }
          return (
            <MessageBubble
              key={`m-${item.id}`}
              message={item.message}
              contactName={conv.contact.name}
              agentsByUuid={agentsByUuid}
            />
          );
        })}
      </div>

      <Composer
        conversation={conv}
        agents={agents}
        refresh={refresh}
        dispatch={dispatch}
        onPatch={onPatch}
        onMentionOpen={onMentionOpen}
      />
    </div>
  );
}

// ------------------------------------------------------------------ accordion
function Accordion({ title, defaultOpen, children }) {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <div className="si-accordion">
      <div
        className="si-accordion-head"
        role="button"
        tabIndex={0}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((o) => !o);
          }
        }}
      >
        {title}
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </div>
      {open && <div className="si-accordion-body">{children}</div>}
    </div>
  );
}

function NotesSection({ notes, agentsByUuid, agents, onAdd }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState('');
  const [files, setFiles] = useState([]);
  const fileRef = useRef(null);

  const cancel = () => {
    setText('');
    setFiles([]);
    setEditing(false);
  };
  const save = () => {
    const body = text.trim();
    const attachment_ids = files.filter((file) => file.id).map((file) => file.id);
    if (!body && attachment_ids.length === 0) return;
    onAdd(body, attachment_ids);
    cancel();
  };
  const onFilePick = (e) => {
    const picked = Array.from(e.target.files || []);
    picked.forEach((file) => {
      const entry = { name: file.name, id: null, progress: 0, failed: false };
      setFiles((prev) => [...prev, entry]);
      uploadAttachment(file, (pct) =>
        setFiles((prev) => prev.map((item) => (item === entry ? { ...item, progress: pct } : item)))
      )
        .then((res) =>
          setFiles((prev) =>
            prev.map((item) =>
              item.name === entry.name && !item.id ? { ...item, id: res.id, progress: 100 } : item
            )
          )
        )
        .catch(() =>
          setFiles((prev) =>
            prev.map((item) =>
              item.name === entry.name && !item.id ? { ...item, failed: true } : item
            )
          )
        );
    });
    e.target.value = '';
  };
  const removeFile = (idx) => setFiles((prev) => prev.filter((_, index) => index !== idx));
  const anyUploading = files.some((file) => !file.id && !file.failed);

  return (
    <>
      {editing ? (
        <div className="si-note-editor">
          <MentionTextarea
            className="si-note-textarea"
            autoFocus
            placeholder="Type @ to mention. Ctrl + Enter to save."
            value={text}
            onChange={setText}
            agents={agents}
            onSubmit={save}
          />
          {files.length > 0 && (
            <div className="si-attachments">
              {files.map((file, index) => (
                <span key={`${file.name}-${index}`} className="si-tag-chip si-att-chip">
                  <Paperclip size={12} /> {file.name}
                  {file.failed && <span className="si-att-progress si-att-failed">failed</span>}
                  {!file.failed && !file.id && (
                    <span className="si-att-progress">{file.progress}%</span>
                  )}
                  <button
                    type="button"
                    aria-label={`Remove ${file.name}`}
                    onClick={() => removeFile(index)}
                  >
                    <X size={13} />
                  </button>
                </span>
              ))}
            </div>
          )}
          <div className="si-note-editor-foot">
            <button
              type="button"
              className="si-tool-btn"
              aria-label="Attach file"
              onClick={() => fileRef.current && fileRef.current.click()}
            >
              <Paperclip size={16} />
            </button>
            <input ref={fileRef} type="file" multiple hidden onChange={onFilePick} />
            <button type="button" className="si-note-btn" onClick={cancel}>
              Cancel
            </button>
            <button
              type="button"
              className="si-note-btn si-note-btn-primary"
              disabled={anyUploading}
              onClick={save}
            >
              Save note
            </button>
          </div>
        </div>
      ) : (
        <button type="button" className="si-note-add" onClick={() => setEditing(true)}>
          <Plus size={14} /> New private note
        </button>
      )}
      {notes.length === 0 && !editing && (
        <span style={{ color: 'var(--text-muted)' }}>No notes yet.</span>
      )}
      {notes.map((n) => {
        const author = n.author_cms_uuid && agentsByUuid[n.author_cms_uuid];
        return (
          <div key={n.id} className="si-note">
            <div className="si-note-meta">
              {author ? author.label : 'Agent'} · {shortAgo(n.created_at)}
            </div>
            {n.body}
            <NoteAttachments attachments={n.attachments} />
          </div>
        );
      })}
    </>
  );
}

function DetailsPanel({
  data,
  agents,
  agentsByUuid,
  tagPool,
  teams,
  merged,
  onPatch,
  onTags,
  onAddNote,
  onOpenConversation,
  onCollapse
}) {
  if (!data) return <div className="si-col si-details" />;
  const conv = data.conversation;
  const { contact } = conv;
  const student = data.contact && data.contact.student;
  const teacher = data.contact && data.contact.teacher;
  const contactLink = contactProfile(data.contact);

  // Contact phone: resolved students/teachers carry it; guests/unresolved don't.
  const phone = (student && student.phone) || (teacher && teacher.phone) || '';

  // Inbox (team) name, display-only. Null/empty team_uuid → Default Inbox.
  const inboxTeam = conv.team_uuid && (teams || []).find((t) => t.key === conv.team_uuid);
  const inboxName = inboxTeam ? inboxTeam.label : 'Default Inbox';
  const notes = data.notes || [];
  const previous = data.previous_conversations || [];

  const agent = agents.find((a) => a.key === merged.assignee_cms_uuid) || null;
  const priority =
    merged.priority && merged.priority !== 'none'
      ? PRIORITY_OPTIONS.find((p) => p.key === merged.priority)
      : null;

  const attrs = [];
  if (student) {
    if (student.country) attrs.push({ label: 'Country', value: student.country });
    if (student.signup_date)
      attrs.push({ label: 'Signup', value: stamp(student.signup_date) || student.signup_date });
    if (student.credits !== undefined && student.credits !== null)
      attrs.push({
        label: 'Credits',
        value: `${student.credits}${student.credits_currency ? ` ${student.credits_currency}` : ''}`
      });
  }

  return (
    <div className="si-col si-details">
      <div className="si-details-tabs">
        <span className="si-active">Details</span>
        <button
          type="button"
          className="si-collapse-btn si-details-collapse"
          aria-label="Collapse details"
          onClick={onCollapse}
        >
          <PanelLeftClose size={18} className="si-rot180" />
        </button>
      </div>

      <div className="si-contact-head">
        <Avatar className="si-avatar-lg" url={contactAvatar(data.contact)} alt={contact.name} />
        <ContactName href={contactLink.href} type={contactLink.type} className="si-contact-name">
          {contactDisplayName(contact, data.contact)}
        </ContactName>
      </div>

      <div className="si-contact-rows">
        <div className="si-contact-row">
          <Mail size={15} /> {contact.email}
        </div>
        {phone && (
          <div className="si-contact-row">
            <Phone size={15} /> {phone}
          </div>
        )}
        <div className="si-contact-row">
          <Inbox size={15} /> {inboxName}
        </div>
      </div>

      <Accordion title="Actions" defaultOpen>
        <div className="si-actions">
          <SearchSelect
            value={agent}
            options={agents}
            placeholder="Select agent"
            onChange={(a) => onPatch({ assignee_cms_uuid: a.key })}
            renderItem={renderAgent}
          />
          <SearchSelect
            value={priority}
            options={PRIORITY_OPTIONS}
            placeholder="Select priority"
            onChange={(p) => onPatch({ priority: p.key })}
            renderItem={renderPriority}
          />
          <TagsInput tags={merged.tags} pool={tagPool} onChange={(tags) => onTags(tags)} />
        </div>
      </Accordion>
      <Accordion title="Contact attributes" defaultOpen>
        {attrs.length === 0 && (
          <span style={{ color: 'var(--text-muted)' }}>No linked student.</span>
        )}
        {attrs.map((a) => (
          <div key={a.label} className="si-attr">
            <span className="si-attr-label">{a.label}</span>
            <span>{a.value}</span>
          </div>
        ))}
      </Accordion>
      <Accordion title="Private Notes" defaultOpen>
        <NotesSection notes={notes} agentsByUuid={agentsByUuid} agents={agents} onAdd={onAddNote} />
      </Accordion>
      <Accordion title="Previous conversations" defaultOpen>
        {previous.length === 0 && (
          <span style={{ color: 'var(--text-muted)' }}>No previous conversations.</span>
        )}
        {previous.map((p) => (
          <div
            key={p.uuid}
            className="si-prev"
            role="button"
            tabIndex={0}
            onClick={() => onOpenConversation(p.uuid)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onOpenConversation(p.uuid);
              }
            }}
          >
            <span className="si-prev-title">
              #{p.ticket_number} {p.subject}
            </span>
            <span className="si-prev-time">
              {shortAgo(p.created_at)} · {shortAgo(p.last_message_at)}
            </span>
          </div>
        ))}
      </Accordion>
    </div>
  );
}

// ----------------------------------------------------------- create-view modal
// Custom dropdown with a clip-aware popover: it measures the nearest scrollable
// ancestor (the .si-modal, overflow-y:auto / max-height:90vh) and caps the menu
// height / flips it up when there's more room above — otherwise the modal's
// scroll viewport clips this absolutely-positioned panel. `options` is a list of
// { value, label, desc? }; desc renders a muted second line when present. Shared
// by the field picker and the operator picker in the filter builder.
function PickDropdown({
  value,
  onChange,
  placeholder,
  options,
  disabled,
  displayValue,
  menuClassName,
  multi
}) {
  const { open, setOpen, ref } = useDropdown();
  const triggerRef = useRef(null);
  const [menu, setMenu] = useState({ up: false, maxHeight: null });

  useLayoutEffect(() => {
    if (!open) return;
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    let clipTop = 0;
    let clipBottom = window.innerHeight;
    for (let node = el.parentElement; node; node = node.parentElement) {
      const oy = window.getComputedStyle(node).overflowY;
      if (oy === 'auto' || oy === 'scroll' || oy === 'hidden') {
        const cr = node.getBoundingClientRect();
        clipTop = cr.top;
        clipBottom = cr.bottom;
        break;
      }
    }
    const margin = 8;
    const spaceBelow = clipBottom - rect.bottom - margin;
    const spaceAbove = rect.top - clipTop - margin;
    const up = spaceBelow < SELECT_MENU_MIN && spaceAbove > spaceBelow;
    setMenu({ up, maxHeight: Math.max(160, Math.floor(up ? spaceAbove : spaceBelow)) });
  }, [open]);

  const selected = multi ? (Array.isArray(value) ? value : []) : [];
  const hasValue = multi ? selected.length > 0 : !!value;
  let display = '';
  if (multi) {
    display = selected
      .map((v) => {
        const opt = options.find((o) => o.value === v);
        return opt ? opt.label : v;
      })
      .join(', ');
  } else if (value) {
    display = displayValue ? displayValue(value) : value;
  }
  return (
    <div
      className={`si-field-select si-fieldpick ${disabled ? 'si-field-select-disabled' : ''}`}
      ref={ref}
    >
      <button
        ref={triggerRef}
        type="button"
        className="si-fieldpick-trigger"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
      >
        <span className={hasValue ? 'si-multi-label' : 'si-select-ph'}>
          {hasValue ? display : placeholder}
        </span>
        <ChevronsUpDown size={15} className="si-select-caret" />
      </button>
      {open && !disabled && (
        <div
          className={`si-select-menu si-fieldpick-menu ${menuClassName || ''} ${
            menu.up ? 'si-select-menu-up' : ''
          }`}
          style={menu.maxHeight ? { maxHeight: `${menu.maxHeight}px` } : undefined}
        >
          <div className="si-select-options">
            {options.map((o) => {
              const isSel = multi ? selected.includes(o.value) : value === o.value;
              return (
                <button
                  key={o.value}
                  type="button"
                  className={`si-select-option si-fieldpick-option ${isSel ? 'si-active' : ''} ${
                    multi ? 'si-multi-option' : ''
                  }`}
                  onClick={() => {
                    if (multi) {
                      // Toggle; keep the menu open so several values can be picked.
                      onChange(
                        isSel ? selected.filter((v) => v !== o.value) : [...selected, o.value]
                      );
                      return;
                    }
                    onChange(o.value);
                    setOpen(false);
                  }}
                >
                  {multi && (
                    <span className={`si-check ${isSel ? 'si-check-on' : ''}`} aria-hidden="true" />
                  )}
                  <span className="si-fieldpick-name">{o.label}</span>
                  {o.desc && <span className="si-fieldpick-desc">{o.desc}</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function FieldPickDropdown({ value, onChange }) {
  return (
    <PickDropdown
      value={value}
      onChange={onChange}
      placeholder="Select field"
      displayValue={fieldLabel}
      options={FILTER_FIELD_DEFS.map((f) => ({
        value: f.name,
        label: f.label || f.name,
        desc: f.desc
      }))}
    />
  );
}

function ConditionValue({ field, operator, value, value2, values, onChange, valueConfig }) {
  const cfg = valueConfig(field);
  if (cfg.type === 'options') {
    // Custom dropdown (same PickDropdown that powers the field/operator pickers)
    // instead of a native <select>. Option values ARE the labels (the option
    // list is label strings), so buildPayload's label->value mapping is unchanged.
    // For contains / not contains the picker is a multi-select whose array of
    // labels is stored in `values`.
    if (operatorIsMulti(operator)) {
      return (
        <PickDropdown
          multi
          value={values || []}
          placeholder="Select values"
          menuClassName="si-valpick-menu"
          options={cfg.options.map((o) => ({ value: o, label: o }))}
          onChange={(vals) => onChange({ values: vals })}
        />
      );
    }
    return (
      <PickDropdown
        value={value}
        placeholder="Select value"
        menuClassName="si-valpick-menu"
        options={cfg.options.map((o) => ({ value: o, label: o }))}
        onChange={(v) => onChange({ value: v })}
      />
    );
  }
  if (cfg.type === 'datetime') {
    return (
      <>
        <input
          type="datetime-local"
          className="si-cond-input"
          value={value}
          onChange={(e) => onChange({ value: e.target.value })}
        />
        {operator === 'between' && (
          <>
            <span className="si-cond-sep">and</span>
            <input
              type="datetime-local"
              className="si-cond-input"
              value={value2}
              onChange={(e) => onChange({ value2: e.target.value })}
            />
          </>
        )}
      </>
    );
  }
  return (
    <input
      type="text"
      className="si-cond-input"
      placeholder="Value"
      value={value}
      onChange={(e) => onChange({ value: e.target.value })}
    />
  );
}

function CreateViewModal({ onClose, onSave, agents, teams, tagPool, editView }) {
  const editMode = !!editView;
  const [name, setName] = useState(editMode ? editView.name || '' : '');
  const [isShared, setIsShared] = useState(editMode ? !!editView.is_shared : false);
  const [groups, setGroups] = useState(() =>
    editMode ? seedGroupsFromFilters(editView.filters, teams, agents) : [newGroup()]
  );

  // Options per field; Assign team/agent show display names (mapped to uuids on save).
  const valueConfig = useCallback(
    (field) => {
      if (DATE_FIELDS.has(field)) return { type: 'datetime' };
      switch (field) {
        case 'Status':
          return { type: 'options', options: STATUS_OPTIONS.map((s) => s.label) };
        case 'Priority':
          return { type: 'options', options: PRIORITY_OPTIONS.map((p) => p.label) };
        case 'User Type':
          return { type: 'options', options: USER_TYPE_OPTIONS.map((u) => u.label) };
        case 'Support Email':
          return { type: 'options', options: SUPPORT_EMAIL_OPTIONS.map((s) => s.label) };
        case 'Student Membership':
          return { type: 'options', options: MEMBERSHIP_OPTIONS.map((m) => m.label) };
        case 'Assign team':
          return { type: 'options', options: teams.map((t) => t.label) };
        case 'Assign agent':
          return { type: 'options', options: agents.map((a) => a.label) };
        case 'Tags':
          return { type: 'options', options: tagPool };
        default:
          return { type: 'string' };
      }
    },
    [agents, teams, tagPool]
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const patchCondition = (gid, cid, patch) =>
    setGroups((gs) =>
      gs.map((g) =>
        g.id === gid
          ? {
              ...g,
              conditions: g.conditions.map((c) => (c.id === cid ? { ...c, ...patch } : c))
            }
          : g
      )
    );
  const addCondition = (gid) =>
    setGroups((gs) =>
      gs.map((g) => (g.id === gid ? { ...g, conditions: [...g.conditions, newCondition()] } : g))
    );
  const removeCondition = (gid, cid) =>
    setGroups((gs) =>
      gs
        .map((g) =>
          g.id === gid ? { ...g, conditions: g.conditions.filter((c) => c.id !== cid) } : g
        )
        .filter((g) => g.conditions.length > 0)
    );
  const addGroup = () => setGroups((gs) => [...gs, newGroup()]);

  // Map a single UI label to the canonical value the backend expects. Assign
  // team/agent send the UUID (not the display name); Support Email / Student
  // Membership send their canonical key; Status/Priority pass the label through
  // (the compiler accepts labels).
  const mapValueForField = (field, label) => {
    if (field === 'Assign team') {
      const t = teams.find((x) => x.label === label);
      return t ? t.key : label;
    }
    if (field === 'Assign agent') {
      const a = agents.find((x) => x.label === label);
      return a ? a.key : label;
    }
    if (field === 'Support Email') {
      const s = SUPPORT_EMAIL_OPTIONS.find((x) => x.label === label);
      return s ? s.key : label;
    }
    if (field === 'Student Membership') {
      const m = MEMBERSHIP_OPTIONS.find((x) => x.label === label);
      return m ? m.key : label;
    }
    return label;
  };

  // Map the UI condition tree to the backend payload. Operators are canonicalised
  // to underscore tokens. contains / not contains emit a `values` array; every
  // other operator emits the single `value` (and value2 for between).
  const buildPayload = () => {
    const outGroups = groups
      .map((g) => ({
        conditions: g.conditions
          .filter((c) => c.field && c.operator)
          .map((c) => {
            const base = {
              field: c.field,
              operator: OPERATOR_TOKEN[c.operator] || c.operator
            };
            if (operatorIsMulti(c.operator)) {
              return {
                ...base,
                values: (c.values || []).map((v) => mapValueForField(c.field, v)),
                value: '',
                value2: ''
              };
            }
            return {
              ...base,
              value: operatorNeedsValue(c.operator) ? mapValueForField(c.field, c.value) : '',
              value2: c.operator === 'between' ? c.value2 : ''
            };
          })
      }))
      .filter((g) => g.conditions.length > 0);
    return { name: name.trim(), is_shared: isShared, groups: outGroups };
  };

  return (
    <div className="si-modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="si-modal si-modal-view"
        role="dialog"
        aria-modal="true"
        aria-label={editMode ? 'Edit View' : 'Create View'}
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="si-modal-close" aria-label="Close" onClick={onClose}>
          <X size={20} />
        </button>

        <h2 className="si-modal-title">{editMode ? 'Edit View' : 'Create View'}</h2>
        <p className="si-modal-sub">
          {editMode
            ? 'Update this view’s name, visibility, and filters.'
            : 'Create and save custom filter views for quick access to your conversations.'}
        </p>

        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
        <label className="si-form-label" htmlFor="si-view-name">
          Name
        </label>
        <input
          id="si-view-name"
          className="si-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <p className="si-form-help">Enter an unique name for your view.</p>

        <div className="si-form-label" id="si-view-visibility-label">
          Visibility
        </div>
        <div
          className="si-composer-tabs"
          role="radiogroup"
          aria-labelledby="si-view-visibility-label"
        >
          <button
            type="button"
            role="radio"
            aria-checked={!isShared}
            className={`si-tab ${!isShared ? 'si-active' : ''}`}
            onClick={() => setIsShared(false)}
          >
            Private to yourself
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={isShared}
            className={`si-tab ${isShared ? 'si-active' : ''}`}
            onClick={() => setIsShared(true)}
          >
            Public with everyone
          </button>
        </div>

        <div className="si-form-label">Filters</div>
        {groups.map((g) => (
          <div key={g.id} className="si-filter-group">
            {g.conditions.map((c) => (
              <div key={c.id} className="si-filter-cond">
                <FieldPickDropdown
                  value={c.field}
                  onChange={(field) =>
                    patchCondition(g.id, c.id, { field, operator: '', value: '', value2: '' })
                  }
                />
                <PickDropdown
                  value={c.operator}
                  placeholder="Select operator"
                  menuClassName="si-oppick-menu"
                  options={operatorsForField(c.field).map((op) => ({ value: op, label: op }))}
                  disabled={!c.field}
                  onChange={(operator) => patchCondition(g.id, c.id, operatorPatch(c, operator))}
                />
                {operatorNeedsValue(c.operator) && (
                  <ConditionValue
                    field={c.field}
                    operator={c.operator}
                    value={c.value}
                    value2={c.value2}
                    values={c.values}
                    valueConfig={valueConfig}
                    onChange={(patch) => patchCondition(g.id, c.id, patch)}
                  />
                )}
                <button
                  type="button"
                  className="si-cond-remove"
                  aria-label="Remove condition"
                  onClick={() => removeCondition(g.id, c.id)}
                >
                  <X size={16} />
                </button>
              </div>
            ))}
            <button type="button" className="si-ghost-btn" onClick={() => addCondition(g.id)}>
              <Plus size={16} /> Add condition
            </button>
          </div>
        ))}

        <button type="button" className="si-ghost-btn si-add-group" onClick={addGroup}>
          <Plus size={16} /> Add group
        </button>
        <p className="si-form-help">Set one or more filters to customize view.</p>

        <div className="si-modal-foot">
          <button type="button" className="si-modal-save" onClick={() => onSave(buildPayload())}>
            {editMode ? 'Save changes' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------------- search overlay
function SearchOverlay({ navCollapsed, onToggleNav, onOpen, dispatch }) {
  const [raw, setRaw] = useState('');
  const [tab, setTab] = useState('conversations');
  const results = useSelector((s) => s.support.search_results);
  const searchStatus = useSelector((s) => s.support.search_status);
  const q = raw.trim();

  // Debounce input → server search (§7).
  useEffect(() => {
    if (!q) return undefined;
    const t = setTimeout(() => dispatch(actions.search_support({ q })), 250);
    return () => clearTimeout(t);
  }, [q, dispatch]);

  const convMatches = q ? results.conversations || [] : [];
  const msgMatches = q ? results.messages || [] : [];

  return (
    <div className="si-col si-search">
      <div className="si-search-head">
        <button
          type="button"
          className="si-collapse-btn"
          aria-label={navCollapsed ? 'Expand inbox bar' : 'Collapse inbox bar'}
          onClick={onToggleNav}
        >
          {navCollapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
        </button>
        <Search size={16} className="si-search-ico" />
        <input
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          className="si-search-input"
          placeholder="Search"
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
        />
      </div>

      {!q ? (
        <div className="si-search-empty">
          <h2>Search conversations</h2>
          <p>Search by reference number, contact email address or messages in conversations.</p>
        </div>
      ) : (
        <div className="si-search-body">
          <div className="si-search-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'conversations'}
              className={`si-search-tab ${tab === 'conversations' ? 'si-active' : ''}`}
              onClick={() => setTab('conversations')}
            >
              Conversations ({convMatches.length})
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'messages'}
              className={`si-search-tab ${tab === 'messages' ? 'si-active' : ''}`}
              onClick={() => setTab('messages')}
            >
              Messages ({msgMatches.length})
            </button>
          </div>

          {tab === 'conversations' ? (
            <div className="si-search-results">
              {searchStatus === 'loading' && convMatches.length === 0 && (
                <div className="si-search-none">Searching…</div>
              )}
              {searchStatus !== 'loading' && convMatches.length === 0 && (
                <div className="si-search-none">No conversations found.</div>
              )}
              {convMatches.map((c) => (
                <button
                  key={c.uuid}
                  type="button"
                  className="si-sr-card"
                  onClick={() => onOpen(c.uuid)}
                >
                  <div className="si-sr-main">
                    <div className="si-sr-top">
                      <span className="si-sr-id">#{c.ticket_number}</span>
                      <span className="si-sr-status">{statusLabel(c.status)}</span>
                    </div>
                    <div className="si-sr-time">
                      <Clock size={14} /> {c.subject}
                    </div>
                  </div>
                  <span className="si-sr-go">
                    <ChevronRight size={16} />
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <div className="si-search-results">
              {msgMatches.length === 0 && <div className="si-search-none">No messages found.</div>}
              {msgMatches.map((m) => (
                <button
                  key={m.message_uuid}
                  type="button"
                  className="si-sr-card"
                  onClick={() => onOpen(m.conversation_uuid)}
                >
                  <div className="si-sr-main">
                    <div className="si-sr-top">
                      <span className="si-sr-id">#{m.ticket_number}</span>
                      <span className="si-sr-author">{m.contact ? m.contact.name : ''}</span>
                    </div>
                    <div className="si-sr-snippet">{m.snippet}</div>
                  </div>
                  <span className="si-sr-go">
                    <ChevronRight size={16} />
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// -------------------------------------------------------------- settings modal
// Two-step "arm, then confirm" helper for destructive/side-effectful controls.
// arm(id) marks a single control as armed (its label flips to a "Click again to
// confirm" state); a second click on the same id is the real action. Only one id
// is ever armed at a time, so arming a different control disarms the previous one.
// The armed state auto-disarms after timeoutMs, on disarm() (mouse-leave), and on
// unmount — the timeout is always cleared to avoid setState-after-unmount.
function useArmedConfirm(timeoutMs = 4000) {
  const [armedId, setArmedId] = useState(null);
  const timerRef = useRef(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const disarm = useCallback(() => {
    clearTimer();
    setArmedId(null);
  }, [clearTimer]);

  const arm = useCallback(
    (id) => {
      clearTimer();
      setArmedId(id);
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        setArmedId(null);
      }, timeoutMs);
    },
    [clearTimer, timeoutMs]
  );

  useEffect(() => clearTimer, [clearTimer]);

  // While something is armed, Escape disarms it (in addition to the × button,
  // the 4s auto-reset and mouse-leave).
  useEffect(() => {
    if (armedId == null) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') disarm();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [armedId, disarm]);

  return { armedId, arm, disarm };
}

// Teams section of the Support Settings modal: list teams + members, create a
// team, delete a team (conversations fall back to the Default Inbox), and
// add/remove members. Every mutation is super-user only (server-enforced); the
// UI is hidden for non-super-users. Reloads the team list after each mutation
// and calls onChanged so the page's left-nav Inboxes refresh too.
function TeamsSettings({ onChanged }) {
  const dispatch = useDispatch();
  const [teams, setTeams] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loadStatus, setLoadStatus] = useState('loading');
  const [newName, setNewName] = useState('');
  const [createError, setCreateError] = useState('');
  const [busy, setBusy] = useState(false);
  const { armedId, arm, disarm } = useArmedConfirm();

  const reload = useCallback(
    (notify) => {
      setLoadStatus((s) => (s === 'success' ? s : 'loading'));
      return fetchTeams()
        .then((rows) => {
          setTeams(rows);
          setLoadStatus('success');
          if (notify) onChanged();
        })
        .catch(() => setLoadStatus('fail'));
    },
    [onChanged]
  );

  useEffect(() => {
    reload(false);
    fetchEligibleAgents()
      .then(setAgents)
      .catch(() => setAgents([]));
  }, [reload]);

  const onCreate = () => {
    const name = newName.trim();
    setCreateError('');
    if (!name) {
      setCreateError('Team name is required');
      return;
    }
    setBusy(true);
    createTeam(name)
      .then(() => {
        setNewName('');
        return reload(true);
      })
      .catch((err) => setCreateError(apiErrorReason(err, 'Could not create team')))
      .finally(() => setBusy(false));
  };

  const onDelete = (team) => {
    // First click arms; second click on the armed team runs the real confirm.
    if (armedId !== team.uuid) {
      arm(team.uuid);
      return;
    }
    disarm();
    const msg = `Delete the team "${team.name}"? Its conversations move to the Default Inbox. This can't be undone.`;
    // eslint-disable-next-line no-alert
    if (!window.confirm(msg)) return;
    setBusy(true);
    deleteTeam(team.uuid)
      .then(() => reload(true))
      .catch((err) =>
        dispatch(
          actions.set_notification_variable(
            true,
            'error',
            apiErrorReason(err, 'Could not delete team')
          )
        )
      )
      .finally(() => setBusy(false));
  };

  const onAddMember = (team, agent) => {
    setBusy(true);
    addTeamMember(team.uuid, agent.key)
      .then(() => reload(true))
      .catch((err) =>
        dispatch(
          actions.set_notification_variable(
            true,
            'error',
            apiErrorReason(err, 'Could not add member')
          )
        )
      )
      .finally(() => setBusy(false));
  };

  const onRemoveMember = (team, member) => {
    setBusy(true);
    removeTeamMember(team.uuid, member.cms_user_uuid)
      .then(() => reload(true))
      .catch((err) =>
        dispatch(
          actions.set_notification_variable(
            true,
            'error',
            apiErrorReason(err, 'Could not remove member')
          )
        )
      )
      .finally(() => setBusy(false));
  };

  return (
    <div className="si-settings-section">
      <h3 className="si-settings-heading">Teams</h3>
      <p className="si-settings-desc">
        Create teams and manage their members. Each team gets its own inbox in the left nav; a user
        can belong to multiple teams.
      </p>

      <div className="si-team-create">
        <input
          className="si-input"
          placeholder="New team name"
          value={newName}
          onChange={(e) => {
            setNewName(e.target.value);
            if (createError) setCreateError('');
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onCreate();
          }}
        />
        <button
          type="button"
          className="si-modal-save"
          onClick={onCreate}
          disabled={busy || !newName.trim()}
        >
          <Plus size={16} /> Create team
        </button>
      </div>
      {createError && <p className="si-team-error">{createError}</p>}

      {loadStatus === 'loading' && teams.length === 0 && (
        <div className="si-state-msg">Loading teams…</div>
      )}
      {loadStatus === 'fail' && <div className="si-state-msg">Couldn&apos;t load teams.</div>}
      {loadStatus === 'success' && teams.length === 0 && (
        <div className="si-state-msg">No teams yet. Create one above.</div>
      )}

      <div className="si-team-list">
        {teams.map((team) => {
          const memberUuids = new Set((team.members || []).map((m) => m.cms_user_uuid));
          const addable = agents
            .filter((a) => !memberUuids.has(a.uuid))
            .map((a) => ({ key: a.uuid, label: a.name || a.email }));
          return (
            <div key={team.uuid} className="si-team-card" onMouseLeave={disarm}>
              <div className="si-team-card-head">
                <span className="si-team-card-name">
                  <Users size={16} /> {team.name}
                </span>
                <span className="si-del-wrap">
                  <button
                    type="button"
                    className={`si-team-del ${armedId === team.uuid ? 'si-del-armed' : ''}`}
                    aria-label={
                      armedId === team.uuid
                        ? `Confirm delete team ${team.name}`
                        : `Delete team ${team.name}`
                    }
                    title={armedId === team.uuid ? 'Click again to confirm' : 'Delete team'}
                    onClick={() => onDelete(team)}
                    disabled={busy}
                  >
                    {armedId === team.uuid ? (
                      <>
                        <Trash2 size={14} /> Confirm delete?
                      </>
                    ) : (
                      <Trash2 size={15} />
                    )}
                  </button>
                  {armedId === team.uuid && (
                    <button
                      type="button"
                      className="si-arm-cancel"
                      aria-label="Cancel"
                      title="Cancel"
                      onClick={disarm}
                      disabled={busy}
                    >
                      <X size={14} />
                    </button>
                  )}
                </span>
              </div>

              <div className="si-team-members">
                {(team.members || []).length === 0 && (
                  <span className="si-team-empty">No members yet.</span>
                )}
                {(team.members || []).map((m) => (
                  <span key={m.cms_user_uuid} className="si-member-chip">
                    {m.name || m.cms_user_uuid}
                    <button
                      type="button"
                      className="si-member-remove"
                      aria-label={`Remove ${m.name || 'member'}`}
                      title="Remove member"
                      onClick={() => onRemoveMember(team, m)}
                      disabled={busy}
                    >
                      <X size={13} />
                    </button>
                  </span>
                ))}
              </div>

              <div className="si-team-add">
                <SearchSelect
                  value={null}
                  options={addable}
                  placeholder="Add a member…"
                  onChange={(a) => onAddMember(team, a)}
                  renderItem={(a) => <span className="si-opt">{a.label}</span>}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Routing rules section of the Support Settings modal. Rules run top-to-bottom on
// new inbound emails, first match wins; empty match fields match everything. Each
// rule matches on To-address / subject-substring / sender-domain and applies
// team / assignee / priority / tag actions. Super-user only (server-enforced).
// Mirrors TeamsSettings: local list + status + busy state, reload() after every
// mutation, window.confirm for deletes, errors via apiErrorReason + toast.
const EMPTY_RULE_FORM = {
  to: '',
  subject: '',
  domain: '',
  teamKey: '',
  assigneeKey: '',
  priorityKey: '',
  tags: ''
};

// Blank a priority action ('none' is the picker's "No priority" option). Both the
// empty key and 'none' mean "don't set a priority" for a routing action.
const rulePriorityIsSet = (key) => !!key && key !== 'none';

// Label for the active/inactive toggle pill. When armed (first click) it shows
// the pending action so the second click reads as an explicit confirm.
const ruleToggleLabel = (rule, armed) => {
  if (armed) return rule.is_active ? 'Confirm deactivate?' : 'Confirm activate?';
  return rule.is_active ? 'Active' : 'Inactive';
};

function RoutingSettings({ agents, teams }) {
  const dispatch = useDispatch();
  const [rules, setRules] = useState([]);
  const [loadStatus, setLoadStatus] = useState('loading');
  const [busy, setBusy] = useState(false);
  // editing: null (none) | 'new' | a rule id.
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_RULE_FORM);
  const [formError, setFormError] = useState('');
  // One armed control at a time across the section; ids are namespaced by action
  // ('toggle:<id>' / 'del:<id>') so arming a delete disarms a pending toggle.
  const { armedId, arm, disarm } = useArmedConfirm();

  const teamByKey = useMemo(() => {
    const m = {};
    teams.forEach((t) => {
      m[t.key] = t;
    });
    return m;
  }, [teams]);
  const agentByKey = useMemo(() => {
    const m = {};
    agents.forEach((a) => {
      m[a.key] = a;
    });
    return m;
  }, [agents]);

  const reload = useCallback(() => {
    setLoadStatus((s) => (s === 'success' ? s : 'loading'));
    return fetchRoutingRules()
      .then((rows) => {
        setRules(rows);
        setLoadStatus('success');
      })
      .catch(() => setLoadStatus('fail'));
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const toast = (err, fallback) =>
    dispatch(actions.set_notification_variable(true, 'error', apiErrorReason(err, fallback)));

  const startCreate = () => {
    setForm(EMPTY_RULE_FORM);
    setFormError('');
    setEditing('new');
  };

  const startEdit = (rule) => {
    setForm({
      to: rule.match_to_address || '',
      subject: rule.match_subject_contains || '',
      domain: rule.match_contact_domain || '',
      teamKey: rule.action_team_uuid || '',
      assigneeKey: rule.action_assignee_cms_uuid || '',
      priorityKey: rulePriorityIsSet(rule.action_priority) ? rule.action_priority : '',
      tags: rule.action_tags || ''
    });
    setFormError('');
    setEditing(rule.id);
  };

  const cancelEdit = () => {
    setEditing(null);
    setForm(EMPTY_RULE_FORM);
    setFormError('');
  };

  const patchForm = (patch) => {
    setForm((f) => ({ ...f, ...patch }));
    if (formError) setFormError('');
  };

  const hasAction = () =>
    !!form.teamKey ||
    !!form.assigneeKey ||
    rulePriorityIsSet(form.priorityKey) ||
    !!form.tags.trim();

  // Build the request body. For a PATCH an explicit empty string clears a field;
  // for a POST empties are sent as null (the server treats empty strings as null
  // there anyway). Actions default to the same blank sentinel.
  const buildPayload = (forPatch) => {
    const blank = forPatch ? '' : null;
    const text = (v) => v.trim() || blank;
    return {
      match_to_address: text(form.to),
      match_subject_contains: text(form.subject),
      match_contact_domain: text(form.domain),
      action_team_uuid: form.teamKey || blank,
      action_assignee_cms_uuid: form.assigneeKey || blank,
      action_priority: rulePriorityIsSet(form.priorityKey) ? form.priorityKey : blank,
      action_tags: form.tags.trim() || blank
    };
  };

  const onSave = () => {
    setFormError('');
    if (!hasAction()) {
      setFormError('Set at least one action (team, assignee, priority or tags).');
      return;
    }
    setBusy(true);
    const done = () => {
      cancelEdit();
      return reload();
    };
    const req =
      editing === 'new'
        ? createRoutingRule({ ...buildPayload(false), is_active: true })
        : updateRoutingRule(editing, buildPayload(true));
    req
      .then(done)
      .catch((err) => setFormError(apiErrorReason(err, 'Could not save rule')))
      .finally(() => setBusy(false));
  };

  const onToggleActive = (rule) => {
    const id = `toggle:${rule.id}`;
    // First click arms; second click on the same pill runs the real confirm.
    if (armedId !== id) {
      arm(id);
      return;
    }
    disarm();
    const msg = rule.is_active
      ? 'Deactivate this routing rule? New inbound emails will no longer match it.'
      : 'Activate this routing rule?';
    // eslint-disable-next-line no-alert
    if (!window.confirm(msg)) return;
    setBusy(true);
    updateRoutingRule(rule.id, { is_active: !rule.is_active })
      .then(reload)
      .catch((err) => toast(err, 'Could not update rule'))
      .finally(() => setBusy(false));
  };

  const onDelete = (rule) => {
    const id = `del:${rule.id}`;
    // First click arms; second click on the same trash button runs the confirm.
    if (armedId !== id) {
      arm(id);
      return;
    }
    disarm();
    // eslint-disable-next-line no-alert
    if (!window.confirm('Delete this routing rule? This can’t be undone.')) return;
    setBusy(true);
    deleteRoutingRule(rule.id)
      .then(() => {
        if (editing === rule.id) cancelEdit();
        return reload();
      })
      .catch((err) => toast(err, 'Could not delete rule'))
      .finally(() => setBusy(false));
  };

  const onReorder = (index, dir) => {
    const next = index + dir;
    if (next < 0 || next >= rules.length) return;
    const ids = rules.map((r) => r.id);
    [ids[index], ids[next]] = [ids[next], ids[index]];
    setBusy(true);
    reorderRoutingRules(ids)
      .then(reload)
      .catch((err) => toast(err, 'Could not reorder rules'))
      .finally(() => setBusy(false));
  };

  const matchSummary = (rule) =>
    [
      `To: ${rule.match_to_address || 'Any'}`,
      `Subject contains: ${rule.match_subject_contains || 'Any'}`,
      rule.match_contact_domain ? `Sender domain: ${rule.match_contact_domain}` : 'Any sender'
    ].join(' · ');

  const actionSummary = (rule) => {
    const parts = [];
    if (rule.action_team_uuid) {
      const t = teamByKey[rule.action_team_uuid];
      parts.push(`Team: ${t ? t.label : rule.action_team_uuid}`);
    }
    if (rule.action_assignee_cms_uuid) {
      const a = agentByKey[rule.action_assignee_cms_uuid];
      parts.push(`Assign: ${a ? a.label : rule.action_assignee_cms_uuid}`);
    }
    if (rulePriorityIsSet(rule.action_priority)) {
      parts.push(`Priority: ${priorityLabel(rule.action_priority)}`);
    }
    if (rule.action_tags) parts.push(`Tags: ${rule.action_tags}`);
    return parts.length ? parts.join(' · ') : 'No actions';
  };

  const teamValue = form.teamKey ? teamByKey[form.teamKey] || null : null;
  const agentValue = form.assigneeKey ? agentByKey[form.assigneeKey] || null : null;
  const priorityValue = rulePriorityIsSet(form.priorityKey)
    ? PRIORITY_OPTIONS.find((p) => p.key === form.priorityKey) || null
    : null;

  const editor = (
    <div className="si-rule-editor">
      <div className="si-form-label">Match</div>
      <p className="si-form-help">Leave a field blank to match everything.</p>
      <div className="si-rule-fields">
        <input
          className="si-input"
          placeholder="To address (Any)"
          value={form.to}
          onChange={(e) => patchForm({ to: e.target.value })}
        />
        <input
          className="si-input"
          placeholder="Subject contains (Any)"
          value={form.subject}
          onChange={(e) => patchForm({ subject: e.target.value })}
        />
        <input
          className="si-input"
          placeholder="Sender domain (Any)"
          value={form.domain}
          onChange={(e) => patchForm({ domain: e.target.value })}
        />
      </div>

      <div className="si-form-label">Actions</div>
      <p className="si-form-help">Set at least one action to apply when a rule matches.</p>
      <div className="si-rule-actions">
        <SearchSelect
          value={teamValue}
          options={teams}
          placeholder="Assign team"
          onChange={(t) => patchForm({ teamKey: t.key })}
          renderItem={renderTeam}
        />
        <SearchSelect
          value={agentValue}
          options={agents}
          placeholder="Assign agent"
          onChange={(a) => patchForm({ assigneeKey: a.key })}
          renderItem={renderAgent}
        />
        <SearchSelect
          value={priorityValue}
          options={PRIORITY_OPTIONS}
          placeholder="Set priority"
          onChange={(p) => patchForm({ priorityKey: p.key })}
          renderItem={renderPriority}
        />
        <input
          className="si-input"
          placeholder="Tags (comma-separated)"
          value={form.tags}
          onChange={(e) => patchForm({ tags: e.target.value })}
        />
      </div>

      {formError && <p className="si-team-error">{formError}</p>}

      <div className="si-rule-editor-foot">
        <button type="button" className="si-modal-save" onClick={onSave} disabled={busy}>
          {editing === 'new' ? 'Add rule' : 'Save changes'}
        </button>
        <button type="button" className="si-ghost-btn" onClick={cancelEdit} disabled={busy}>
          Cancel
        </button>
      </div>
    </div>
  );

  return (
    <div className="si-settings-section">
      <h3 className="si-settings-heading">Routing rules</h3>
      <p className="si-settings-desc">
        Rules run top to bottom on every new inbound email and the first one that matches wins. An
        empty match field matches everything.
      </p>

      {editing !== 'new' && (
        <button
          type="button"
          className="si-modal-save si-rule-add"
          onClick={startCreate}
          disabled={busy}
        >
          <Plus size={16} /> Add rule
        </button>
      )}
      {editing === 'new' && editor}

      {loadStatus === 'loading' && rules.length === 0 && (
        <div className="si-state-msg">Loading rules…</div>
      )}
      {loadStatus === 'fail' && (
        <div className="si-state-msg">Couldn&apos;t load routing rules.</div>
      )}
      {loadStatus === 'success' && rules.length === 0 && (
        <div className="si-state-msg">No routing rules yet. Add one above.</div>
      )}

      <div className="si-team-list">
        {rules.map((rule, index) => (
          <div
            key={rule.id}
            className={`si-team-card si-rule-card ${rule.is_active ? '' : 'si-rule-inactive'}`}
            onMouseLeave={disarm}
          >
            {editing === rule.id ? (
              editor
            ) : (
              <div className="si-rule-row">
                <div className="si-rule-arrows">
                  <button
                    type="button"
                    className="si-rule-btn"
                    aria-label="Move rule up"
                    title="Move up"
                    onClick={() => onReorder(index, -1)}
                    disabled={busy || index === 0}
                  >
                    <ChevronUp size={16} />
                  </button>
                  <button
                    type="button"
                    className="si-rule-btn"
                    aria-label="Move rule down"
                    title="Move down"
                    onClick={() => onReorder(index, 1)}
                    disabled={busy || index === rules.length - 1}
                  >
                    <ChevronDown size={16} />
                  </button>
                </div>
                <div className="si-rule-body">
                  <div className="si-rule-match">{matchSummary(rule)}</div>
                  <div className="si-rule-action">{actionSummary(rule)}</div>
                </div>
                <div className="si-rule-ctrls">
                  <span className="si-del-wrap">
                    <button
                      type="button"
                      className={`si-rule-toggle ${rule.is_active ? 'si-active' : ''} ${
                        armedId === `toggle:${rule.id}` ? 'si-toggle-armed' : ''
                      }`}
                      onClick={() => onToggleActive(rule)}
                      disabled={busy}
                    >
                      {ruleToggleLabel(rule, armedId === `toggle:${rule.id}`)}
                    </button>
                    {armedId === `toggle:${rule.id}` && (
                      <button
                        type="button"
                        className="si-arm-cancel"
                        aria-label="Cancel"
                        title="Cancel"
                        onClick={disarm}
                        disabled={busy}
                      >
                        <X size={14} />
                      </button>
                    )}
                  </span>
                  <button
                    type="button"
                    className="si-ghost-btn"
                    onClick={() => startEdit(rule)}
                    disabled={busy}
                  >
                    Edit
                  </button>
                  <span className="si-del-wrap">
                    <button
                      type="button"
                      className={`si-team-del ${
                        armedId === `del:${rule.id}` ? 'si-del-armed' : ''
                      }`}
                      aria-label={
                        armedId === `del:${rule.id}` ? 'Confirm delete rule' : 'Delete rule'
                      }
                      title={
                        armedId === `del:${rule.id}` ? 'Click again to confirm' : 'Delete rule'
                      }
                      onClick={() => onDelete(rule)}
                      disabled={busy}
                    >
                      {armedId === `del:${rule.id}` ? (
                        <>
                          <Trash2 size={14} /> Confirm delete?
                        </>
                      ) : (
                        <Trash2 size={15} />
                      )}
                    </button>
                    {armedId === `del:${rule.id}` && (
                      <button
                        type="button"
                        className="si-arm-cancel"
                        aria-label="Cancel"
                        title="Cancel"
                        onClick={disarm}
                        disabled={busy}
                      >
                        <X size={14} />
                      </button>
                    )}
                  </span>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// Signature section of the Support Settings modal: the agent's OWN outbound email
// signature. Visible to EVERY agent (this is why the popup is no longer
// super-user-gated) and scoped to the caller — the API takes no agent id, so an
// agent can only ever read/write their own.
//
// The signature is appended SERVER-SIDE at send time (goserver
// services/support/signature.go), not injected into the composer: it can't be
// forgotten by another client, can't be half-deleted while editing a draft, and
// the HTML and plain-text parts of the email are guaranteed to match. So this tab
// only edits the stored value.
//
// Same Quill config as the composer (shared modules/formats from src/constants),
// so formatting available here is exactly what the composer can render. The saved
// value is re-read from the PUT response rather than kept locally, because the
// server sanitizes on write — the editor must show what was actually stored.
function SignatureSettings() {
  const [html, setHtml] = useState('');
  const [loadStatus, setLoadStatus] = useState('loading');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const quillRef = useRef(null);

  useQuillPaste(quillRef, loadStatus === 'success');

  useEffect(() => {
    let live = true;
    fetchMySignature()
      .then((row) => {
        if (!live) return;
        setHtml(row.body_html || '');
        setLoadStatus('success');
      })
      .catch(() => {
        if (live) setLoadStatus('fail');
      });
    return () => {
      live = false;
    };
  }, []);

  // Clear the "Saved" flash as soon as the agent edits again, so the tick always
  // refers to the text currently in the editor.
  const onChange = (value) => {
    setHtml(value);
    setSaved(false);
  };

  const onSave = () => {
    setError('');
    setSaving(true);
    // Quill represents an empty editor as "<p><br></p>"; send it through as-is —
    // the server treats a body with no text as "clear my signature".
    saveMySignature(html)
      .then((row) => {
        setHtml(row.body_html || '');
        setSaved(true);
      })
      .catch((err) => setError(apiErrorReason(err, 'Could not save your signature')))
      .finally(() => setSaving(false));
  };

  return (
    <div className="si-settings-section">
      <h3 className="si-settings-heading">Signature</h3>
      <p className="si-settings-desc">
        Your personal signature. It is added to the end of every email you send from the inbox —
        replies and new conversations. Internal notes and automatic acknowledgements never carry it.
      </p>

      {loadStatus === 'loading' && <p className="si-settings-note">Loading your signature…</p>}
      {loadStatus === 'fail' && (
        <p className="si-team-error">Could not load your signature. Close and reopen to retry.</p>
      )}

      {loadStatus === 'success' && (
        <>
          <ReactQuill
            ref={quillRef}
            className="si-quill si-sig-quill"
            theme="snow"
            value={html}
            onChange={onChange}
            modules={quillModules}
            formats={quillFormats}
            placeholder="e.g. Asha Rao — Student Care, MyYogaTeacher"
          />

          <div className="si-sig-foot">
            <button type="button" className="si-modal-save" onClick={onSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save signature'}
            </button>
            {saved && !saving && (
              <span className="si-sig-saved" role="status">
                <Check size={15} /> Saved
              </span>
            )}
          </div>

          {!!error && <p className="si-team-error">{error}</p>}
        </>
      )}
    </div>
  );
}

// Sections of the Support Settings modal, in nav order. `superUserOnly` sections
// are hidden entirely for everyone else (the goserver handlers enforce the same
// rule — see requireSuperUser — so hiding them is UX, not the security boundary).
// Signature is every agent's own setting, which is why the popup itself now opens
// for all support agents.
const SETTINGS_SECTIONS = [
  { key: 'signature', label: 'Signature', icon: PenLine },
  { key: 'teams', label: 'Teams', icon: Users, superUserOnly: true },
  { key: 'routing', label: 'Routing rules', icon: Route, superUserOnly: true }
];

// Support Settings modal — wide modal with a left section nav and a right
// content pane. Open to EVERY support agent for their own Signature; the Teams
// and Routing rules sections are filtered out for non-super-users (the server
// enforces the same rule on the mutations). The opening section is the first one
// the agent can actually see, so a non-super-user lands on Signature and a
// super-user still lands on Teams' neighbour-free default.
function SettingsModal({ onClose, onTeamsChanged, agents, teams, isSuperUser }) {
  const sections = useMemo(
    () => SETTINGS_SECTIONS.filter((s) => !s.superUserOnly || isSuperUser),
    [isSuperUser]
  );
  const [section, setSection] = useState(sections[0].key);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="si-modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="si-modal si-settings-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Support settings"
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="si-modal-close" aria-label="Close" onClick={onClose}>
          <X size={20} />
        </button>
        <h2 className="si-modal-title">Support Settings</h2>

        <div className="si-settings-body">
          <div className="si-settings-nav">
            {sections.map((s) => {
              const IconEl = s.icon;
              return (
                <button
                  key={s.key}
                  type="button"
                  className={`si-settings-nav-item ${section === s.key ? 'si-active' : ''}`}
                  onClick={() => setSection(s.key)}
                >
                  <IconEl size={16} />
                  {s.label}
                </button>
              );
            })}
          </div>
          <div className="si-settings-content">
            {section === 'signature' && <SignatureSettings />}
            {/* Both guarded on isSuperUser as well as the section key: the nav can
                only reach a filtered section, but this keeps the super-user-only
                panes unreachable even if `section` were ever set from elsewhere
                (a deep link, a future remembered-tab preference). */}
            {section === 'teams' && isSuperUser && <TeamsSettings onChanged={onTeamsChanged} />}
            {section === 'routing' && isSuperUser && (
              <RoutingSettings agents={agents} teams={teams} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------- shell
export default function SupportInbox() {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const bootstrap = useSelector((s) => s.support.bootstrap);
  const conversations = useSelector((s) => s.support.conversations);
  const conversationsStatus = useSelector((s) => s.support.conversations_status);
  const conversationsTotal = useSelector((s) => s.support.conversations_total);
  const activeConversation = useSelector((s) => s.support.active_conversation);
  const activeStatus = useSelector((s) => s.support.active_conversation_status);
  const cmsUserEmail = useSelector((s) => s.home.cms_user_email);
  const accessType = useSelector((s) => s.home.access_type);
  const myEmail = (getCookie('cms_email') || cmsUserEmail || '').toLowerCase();
  const isSuperUser = useMemo(() => isSuperUserAccess(accessType), [accessType]);

  const [activeFolder, setActiveFolder] = useState('all');
  const [statusFilter, setStatusFilter] = useState('');
  // Unread-only toggle: an extra narrowing layer on top of folder + status,
  // resolved server-side per agent (hybrid read watermark), not client-side.
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [sort, setSort] = useState('newest_activity');
  const [selectedId, setSelectedId] = useState(null);
  const [navCollapsed, setNavCollapsed] = useState(false);
  const [detailsCollapsed, setDetailsCollapsed] = useState(false);
  const [showCreateView, setShowCreateView] = useState(false);
  // The saved view currently open in the edit modal (null when creating).
  const [editingView, setEditingView] = useState(null);
  const [showNewConversation, setShowNewConversation] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [pending, setPending] = useState({}); // uuid → optimistic action-panel patch

  const agents = useMemo(() => mapAgents(bootstrap.agents, myEmail), [bootstrap.agents, myEmail]);
  const teams = useMemo(() => mapTeams(bootstrap.teams), [bootstrap.teams]);
  const tagPool = useMemo(() => mapTagPool(bootstrap.tags), [bootstrap.tags]);
  const agentsByUuid = useMemo(() => {
    const m = {};
    agents.forEach((a) => {
      m[a.key] = a;
    });
    return m;
  }, [agents]);

  const folderCounts = useMemo(() => bootstrap.folder_counts || {}, [bootstrap.folder_counts]);
  const folders = useMemo(
    () => [
      { key: 'my_inbox', label: 'My Inbox', icon: 'user', count: folderCounts.my_inbox || 0 },
      { key: 'mentions', label: 'Mentions', icon: 'at', count: folderCounts.mentions || 0 },
      {
        key: 'unassigned',
        label: 'Unassigned',
        icon: 'circle',
        count: folderCounts.unassigned || 0
      },
      { key: 'all', label: 'All', icon: 'list', count: folderCounts.all || 0 }
    ],
    [folderCounts]
  );
  // Inboxes nav group: a non-deletable "Default Inbox" (conversations with
  // no team) always leads, followed by one inbox per team. All users see all
  // inboxes (no membership-based hiding).
  const teamFolders = useMemo(
    () => [
      {
        key: DEFAULT_INBOX_KEY,
        label: 'Default Inbox',
        emoji: '',
        count: folderCounts.default_inbox || 0
      },
      ...teams.map((t) => ({
        key: `team:${t.key}`,
        label: t.label,
        emoji: t.emoji,
        count: (folderCounts.teams && folderCounts.teams[t.key]) || 0
      }))
    ],
    [teams, folderCounts]
  );
  const viewFolders = useMemo(
    () =>
      (bootstrap.views || []).map((v) => ({
        key: `view:${v.uuid}`,
        label: v.name,
        uuid: v.uuid,
        // Carried so the edit modal can seed itself from the saved view without a
        // second fetch (name/visibility/filters all come from bootstrap).
        name: v.name,
        is_shared: v.is_shared,
        filters: v.filters,
        count: v.count || 0,
        // View management (create/edit/delete) is restricted to super users, who
        // may manage ANY view regardless of owner. Backend enforces the same
        // (UpdateView/DeleteView allow owner or super-user).
        canDelete: isSuperUser
      })),
    [bootstrap.views, isSuperUser]
  );

  // How many rows the list is currently asking for. "Load more" grows it a page
  // at a time; it is part of listParams, so every refresh path (the filter
  // effect, realtime packets, post-reply/patch reconciles) re-requests the SAME
  // expanded page instead of collapsing the list back to the first 25.
  const [listLimit, setListLimit] = useState(LIST_PAGE_SIZE);
  const [loadingMore, setLoadingMore] = useState(false);
  // Reset to one page whenever the folder/status/unread/sort scope changes.
  // Done during render (not in an effect) so the fetch effect below sees the
  // reset limit on its first run and fires a single request, not two.
  const filterKey = `${activeFolder}|${statusFilter}|${unreadOnly}|${sort}`;
  const [limitScope, setLimitScope] = useState(filterKey);
  if (limitScope !== filterKey) {
    setLimitScope(filterKey);
    setListLimit(LIST_PAGE_SIZE);
  }

  const listParams = useMemo(
    () => ({
      folder: activeFolder,
      status: statusFilter,
      unread: unreadOnly,
      sort,
      limit: listLimit
    }),
    [activeFolder, statusFilter, unreadOnly, sort, listLimit]
  );

  // The button owns its own spinner (the list-level `loading` status also fires
  // for background refreshes, which must not light it up); clear it as soon as
  // any list fetch settles.
  useEffect(() => {
    if (conversationsStatus !== 'loading') setLoadingMore(false);
  }, [conversationsStatus]);

  const onLoadMore = useCallback(() => {
    setLoadingMore(true);
    setListLimit((n) => Math.min(n + LIST_PAGE_SIZE, LIST_MAX_LIMIT));
  }, []);

  // Silent bootstrap refetch (refreshes agents/teams/counts), throttled so
  // repeated triggers don't hammer the endpoint. Realtime packets arrive per
  // event; the server now caches bootstrap counts org-wide and busts that cache
  // on mutations, so responses are cheap and event-fresh. A short 3s client
  // throttle keeps the left-nav badges (folders, teams, saved views) event-fresh
  // during bursts without over-calling. Shared by all trigger paths (realtime
  // packet handler, @mention-open, and tab-refocus) — 3s is fine for each.
  const lastBootstrapRef = useRef(0);
  const refreshBootstrap = useCallback(() => {
    const now = Date.now();
    if (now - lastBootstrapRef.current < 3000) return;
    lastBootstrapRef.current = now;
    dispatch(actions.get_support_bootstrap());
  }, [dispatch]);

  // Bootstrap on mount.
  useEffect(() => {
    lastBootstrapRef.current = Date.now();
    dispatch(actions.get_support_bootstrap());
  }, [dispatch]);

  // Deep-link: a shared ticket link (`/support?tkt=<uuid>`) opens that
  // conversation on mount, mirroring onCreated/search-overlay selection
  // (activeFolder 'all' + selectedId). The param is then stripped via
  // history.replace so refresh/back don't re-trigger it. A bad/unknown uuid
  // surfaces through the normal thread-fetch error path (active_conversation
  // stays empty → the thread pane shows its not-found/error state).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tkt = params.get('tkt');
    if (!tkt) return;
    setActiveFolder('all');
    setSelectedId(tkt);
    params.delete('tkt');
    const rest = params.toString();
    history.replace({ pathname: location.pathname, search: rest ? `?${rest}` : '' });
    // Mount-only: capture the initial deep link exactly once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // (Re)load the list whenever the folder / status / sort changes.
  useEffect(() => {
    dispatch(actions.get_support_conversations(listParams));
  }, [dispatch, listParams]);

  // Auto-select the first row when nothing is selected. A ticket opened from a
  // deep link, search, or the Previous conversations panel may not appear in
  // the currently filtered list, but should remain open in the thread.
  useEffect(() => {
    if (conversationsStatus !== 'success') return;
    if (conversations.length === 0) {
      return;
    }
    if (!selectedId) {
      setSelectedId(conversations[0].uuid);
    }
  }, [conversations, conversationsStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load the selected thread.
  useEffect(() => {
    if (!selectedId) return;
    dispatch(actions.get_support_conversation({ uuid: selectedId }));
  }, [dispatch, selectedId]);

  // Mark-read the open thread — re-fires when its latest entry changes, not
  // only on selection, so a message arriving over the WS while the agent is
  // viewing the conversation clears the badge immediately. The ref dedups on
  // (uuid, entry count, latest entry id): marking read refreshes the counts
  // (get_support_bootstrap), not the timeline, so this can't loop.
  const lastMarkedRef = useRef(null);
  useEffect(() => {
    if (!selectedId) return;
    const conv = activeConversation && activeConversation.conversation;
    // Only act on the thread that's actually open — the store may still hold a
    // previous selection's data until the new fetch lands.
    if (!conv || conv.uuid !== selectedId) return;
    const timeline = (activeConversation && activeConversation.timeline) || [];
    const latestId = timeline.length ? timeline[timeline.length - 1].id : null;
    const marker = `${selectedId}:${timeline.length}:${latestId}`;
    if (lastMarkedRef.current === marker) return;
    lastMarkedRef.current = marker;
    dispatch(actions.mark_support_read({ uuid: selectedId }));
  }, [dispatch, selectedId, activeConversation]);

  // Clear the optimistic override for a conversation once fresh server data lands.
  useEffect(() => {
    const c = activeConversation && activeConversation.conversation;
    if (!c || !c.uuid) return;
    setPending((p) => {
      if (!p[c.uuid]) return p;
      const next = { ...p };
      delete next[c.uuid];
      return next;
    });
  }, [activeConversation]);

  // Realtime updates over the myt_chat WebSocket (myt:se packets) replace the
  // old 30s poll. The listener is registered once; these refs give it the
  // current folder/filters, selection and thread data without re-subscribing on
  // every keystroke.
  const listParamsRef = useRef(listParams);
  const selectedIdRef = useRef(selectedId);
  const activeConversationRef = useRef(activeConversation);
  useEffect(() => {
    listParamsRef.current = listParams;
  }, [listParams]);
  useEffect(() => {
    selectedIdRef.current = selectedId;
  }, [selectedId]);
  useEffect(() => {
    activeConversationRef.current = activeConversation;
  }, [activeConversation]);

  useEffect(() => {
    let listTimer = null;
    let threadTimer = null;

    const refetchList = () => dispatch(actions.get_support_conversations(listParamsRef.current));
    const refetchThread = () => {
      const uuid = selectedIdRef.current;
      if (uuid) dispatch(actions.get_support_conversation({ uuid, silent: true }));
    };
    // Trailing debounce so a burst of N packets collapses into one refetch.
    const scheduleList = () => {
      if (listTimer) clearTimeout(listTimer);
      listTimer = setTimeout(refetchList, 500);
    };
    const scheduleThread = () => {
      if (threadTimer) clearTimeout(threadTimer);
      threadTimer = setTimeout(refetchThread, 500);
    };

    // message.delivery for the OPEN thread: patch the message's
    // delivery_status/opened_at in place (immutably, via redux) so the tick
    // updates without a network round-trip. Falls back to a silent thread
    // refetch when the message can't be located in the current timeline.
    const applyDelivery = (data) => {
      const messageUuid = data.message_uuid;
      const { current } = activeConversationRef;
      const timeline = current && current.timeline;
      if (!messageUuid || !Array.isArray(timeline)) {
        scheduleThread();
        return;
      }
      let matched = false;
      const nextTimeline = timeline.map((item) => {
        const m = item.message;
        if (
          m &&
          (m.uuid === messageUuid || m.message_uuid === messageUuid || item.id === messageUuid)
        ) {
          matched = true;
          return {
            ...item,
            message: {
              ...m,
              delivery_status:
                data.delivery_status != null ? data.delivery_status : m.delivery_status,
              opened_at: data.opened_at != null ? data.opened_at : m.opened_at
            }
          };
        }
        return item;
      });
      if (!matched) {
        scheduleThread();
        return;
      }
      dispatch(
        actions.set_support_variable('active_conversation', { ...current, timeline: nextTimeline })
      );
    };

    const onSupportPacket = (pkt) => {
      const data = (pkt && pkt.data) || {};
      const convUuid = data.conversation_uuid;
      switch (pkt && pkt.event) {
        case 'message.new':
        case 'note.new':
        case 'conversation.updated':
          scheduleList();
          if (convUuid && convUuid === selectedIdRef.current) scheduleThread();
          // Left-nav folder/team/view counts can change on inbound events;
          // refresh them (throttled) so badges don't go stale.
          refreshBootstrap();
          break;
        case 'message.delivery':
          if (convUuid && convUuid === selectedIdRef.current) applyDelivery(data);
          break;
        case 'mention.new':
          dispatch(
            actions.set_notification_variable(
              true,
              'success',
              `You were mentioned in #${data.ticket_number}`
            )
          );
          // A new mention can change the Mentions folder / list contents.
          scheduleList();
          // Refresh left-nav counts (throttled) so the Mentions badge updates.
          refreshBootstrap();
          break;
        default:
          break;
      }
    };
    CallbackHandler.shared.registerSupportEmailListener(onSupportPacket);

    // Resilience (no poll anymore): after a WS reconnect, refetch list + thread
    // once to catch anything missed while disconnected.
    let wasConnected = ChatClient.shared.connectionState() === ConnectionState.AUTHENTICATED;
    const onConnState = (state) => {
      const nowConnected = state === ConnectionState.AUTHENTICATED;
      if (nowConnected && !wasConnected) {
        refetchList();
        refetchThread();
      }
      wasConnected = nowConnected;
    };
    CallbackHandler.shared.registerConnectionListener(onConnState);

    // Safety net: refetch when the tab is refocused (backgrounded tabs can miss
    // pushes / drop the socket).
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        refetchList();
        refetchThread();
        // Also refresh agents/teams/counts (throttled) so a tab left open picks
        // up newly added CMS users without a full reload.
        refreshBootstrap();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    // The chat WS is normally already connected by the app shell
    // (home/index.jsx → ChatClient.initialize). Deep-linking straight into
    // /support could beat that, so connect defensively when we're authed and
    // not yet connected. Idempotent; guarded so a missing token fails quietly.
    try {
      if (
        getAuthUserInfoOrDefault().uuid &&
        ChatClient.shared.connectionState() === ConnectionState.NOT_CONNECTED
      ) {
        Promise.resolve(ChatClient.shared.initialize()).catch(() => {});
      }
    } catch (e) {
      // no auth / already connected as another user — realtime just won't start
    }

    return () => {
      CallbackHandler.shared.unregisterSupportEmailListener(onSupportPacket);
      CallbackHandler.shared.unregisterConnectionListener(onConnState);
      document.removeEventListener('visibilitychange', onVisibility);
      if (listTimer) clearTimeout(listTimer);
      if (threadTimer) clearTimeout(threadTimer);
    };
  }, [dispatch, refreshBootstrap]);

  const activeLabel = useMemo(() => {
    const folder = folders.find((x) => x.key === activeFolder);
    if (folder) return folder.label;
    const team = teamFolders.find((x) => x.key === activeFolder);
    if (team) return activeFolder === DEFAULT_INBOX_KEY ? team.label : `Team: ${team.label}`;
    const view = viewFolders.find((x) => x.key === activeFolder);
    if (view) return `View: ${view.label}`;
    return 'All';
  }, [activeFolder, folders, teamFolders, viewFolders]);

  // Map redux rows → the display shape the list renders, merging optimistic edits.
  const rows = useMemo(
    () =>
      conversations.map((r) => {
        const pend = pending[r.uuid] || {};
        return {
          id: r.uuid,
          avatar: r.contact.avatar || DEFAULT_AVATAR,
          name: contactDisplayName(r.contact),
          channel: r.source === 'email' ? 'Email support' : r.source,
          lastActivity: shortAgo(r.last_activity),
          preview: r.preview,
          unread: r.unread_count,
          priority: pend.priority !== undefined ? pend.priority : r.priority,
          tags: pend.tags !== undefined ? pend.tags : (r.tags || []).map((t) => t.name)
        };
      }),
    [conversations, pending]
  );

  // Merged current values for the actions panel (server value + optimistic patch).
  const merged = useMemo(() => {
    const conv = activeConversation && activeConversation.conversation;
    if (!conv) return { priority: 'none', tags: [], assignee_cms_uuid: null, team_uuid: null };
    const pend = pending[conv.uuid] || {};
    return {
      priority: pend.priority !== undefined ? pend.priority : conv.priority,
      tags: pend.tags !== undefined ? pend.tags : (conv.tags || []).map((t) => t.name),
      assignee_cms_uuid:
        pend.assignee_cms_uuid !== undefined ? pend.assignee_cms_uuid : conv.assignee_cms_uuid,
      team_uuid: pend.team_uuid !== undefined ? pend.team_uuid : conv.team_uuid
    };
  }, [activeConversation, pending]);

  const patchActive = (patch) => {
    const conv = activeConversation && activeConversation.conversation;
    if (!conv) return;
    const { uuid } = conv;
    setPending((p) => ({ ...p, [uuid]: { ...p[uuid], ...patch } }));
    const body = { ...patch };
    // Snoozing needs a wake time; default to +1 day when set from the header.
    if (patch.status === 'snoozed') {
      body.snoozed_until = new Date(Date.now() + 24 * 3600 * 1000).toISOString();
    }
    dispatch(actions.patch_support_conversation({ uuid, patch: body, refresh: listParams }));
  };

  const setTags = (tags) => {
    const conv = activeConversation && activeConversation.conversation;
    if (!conv) return;
    const { uuid } = conv;
    setPending((p) => ({ ...p, [uuid]: { ...p[uuid], tags } }));
    dispatch(actions.replace_support_tags({ uuid, tags, refresh: listParams }));
  };

  const addNote = (body, attachment_ids = []) => {
    const conv = activeConversation && activeConversation.conversation;
    if (!conv) return;
    dispatch(
      actions.add_support_note({
        uuid: conv.uuid,
        body,
        mentions: extractMentions(body, agents),
        attachment_ids
      })
    );
  };

  const openConversation = (uuid) => {
    setActiveFolder('all');
    setStatusFilter('');
    setSelectedId(uuid);
    dispatch(actions.get_support_conversation({ uuid }));
    dispatch(actions.mark_support_read({ uuid }));
  };

  const onSaveView = (payload) => {
    if (!payload.name) {
      dispatch(actions.set_notification_variable(true, 'error', 'View name is required'));
      return;
    }
    if (editingView) {
      // PUT /v2/support/views/:uuid — same body as create; the saga refetches
      // bootstrap on success exactly like the create path.
      dispatch(actions.save_support_view({ method: 'put', uuid: editingView.uuid, body: payload }));
    } else {
      dispatch(actions.save_support_view({ method: 'post', body: payload }));
    }
    setShowCreateView(false);
    setEditingView(null);
  };

  // Open the create-view modal in edit mode, seeded from the saved view.
  const onEditView = (view) => setEditingView(view);

  // Delete a saved view. Confirm (house pattern: window.confirm, as in
  // sms_report), then dispatch — the delete saga refetches bootstrap so the nav
  // list refreshes on success. If the deleted view is the one currently open,
  // fall back to My Inbox so the list isn't left pointing at a gone folder.
  const onDeleteView = (view) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete the view "${view.label}"? This can't be undone.`)) return;
    dispatch(actions.delete_support_view({ uuid: view.uuid }));
    if (activeFolder === view.key) {
      setActiveFolder('my_inbox');
    }
  };

  return (
    <>
      <Helmet>
        <title>Support Inbox · Hridaya</title>
      </Helmet>
      <div
        className={`si-shell ${navCollapsed ? 'si-nav-collapsed' : ''} ${
          detailsCollapsed ? 'si-details-collapsed' : ''
        } ${searchOpen ? 'si-search-open' : ''}`}
      >
        {!navCollapsed && (
          <NavRail
            folders={folders}
            teams={teamFolders}
            views={viewFolders}
            activeFolder={activeFolder}
            onSelectFolder={(k) => {
              setSearchOpen(false);
              setActiveFolder(k);
            }}
            onCreateView={() => setShowCreateView(true)}
            onDeleteView={onDeleteView}
            onEditView={onEditView}
            onSearch={() => setSearchOpen(true)}
            onNewConversation={() => setShowNewConversation(true)}
            canManageViews={isSuperUser}
            onOpenSettings={() => setShowSettings(true)}
          />
        )}
        {searchOpen ? (
          <SearchOverlay
            navCollapsed={navCollapsed}
            onToggleNav={() => setNavCollapsed((v) => !v)}
            dispatch={dispatch}
            onOpen={(uuid) => {
              setActiveFolder('all');
              setSelectedId(uuid);
              setSearchOpen(false);
            }}
          />
        ) : (
          <>
            <ConversationList
              rows={rows}
              status={conversationsStatus}
              selectedId={selectedId}
              onSelect={setSelectedId}
              navCollapsed={navCollapsed}
              onToggleNav={() => setNavCollapsed((v) => !v)}
              title={activeLabel}
              statusFilter={statusFilter}
              onStatusFilter={setStatusFilter}
              unreadOnly={unreadOnly}
              onUnreadOnly={setUnreadOnly}
              sort={sort}
              onSort={setSort}
              count={conversationsTotal}
              hasMore={rows.length < conversationsTotal && listLimit < LIST_MAX_LIMIT}
              loadingMore={loadingMore}
              onLoadMore={onLoadMore}
              atServerCap={listLimit >= LIST_MAX_LIMIT}
            />
            <Thread
              data={activeConversation}
              status={activeStatus}
              agentsByUuid={agentsByUuid}
              agents={agents}
              refresh={listParams}
              dispatch={dispatch}
              onPatch={patchActive}
              onMentionOpen={refreshBootstrap}
            />
            {!detailsCollapsed && (
              <DetailsPanel
                data={activeConversation}
                agents={agents}
                agentsByUuid={agentsByUuid}
                tagPool={tagPool}
                teams={teams}
                merged={merged}
                onPatch={patchActive}
                onTags={setTags}
                onAddNote={addNote}
                onOpenConversation={openConversation}
                onCollapse={() => setDetailsCollapsed(true)}
              />
            )}
            {detailsCollapsed && (
              <button
                type="button"
                className="si-details-expand"
                aria-label="Expand details"
                onClick={() => setDetailsCollapsed(false)}
              >
                <PanelLeftOpen size={18} className="si-rot180" />
              </button>
            )}
          </>
        )}
      </div>
      {(showCreateView || editingView) && isSuperUser && (
        <CreateViewModal
          onClose={() => {
            setShowCreateView(false);
            setEditingView(null);
          }}
          onSave={onSaveView}
          agents={agents}
          teams={teams}
          tagPool={tagPool}
          editView={editingView}
        />
      )}
      {showNewConversation && (
        <NewConversationModal
          onClose={() => setShowNewConversation(false)}
          onCreated={(uuid) => {
            // Open the freshly created thread — mirror the search overlay's
            // open behaviour so it's in scope + selected.
            setActiveFolder('all');
            setSelectedId(uuid);
          }}
          agents={agents}
          teams={teams}
          inboxes={teamFolders}
          refresh={listParams}
          dispatch={dispatch}
        />
      )}
      {showSettings && (
        <SettingsModal
          onClose={() => setShowSettings(false)}
          onTeamsChanged={() => dispatch(actions.get_support_bootstrap())}
          agents={agents}
          teams={teams}
          isSuperUser={isSuperUser}
        />
      )}
    </>
  );
}
